import { useEffect, useState, useCallback, useMemo } from "react";
import { Users, Check, X, Clock, Loader2, MessageSquareText, StickyNote, GraduationCap, Eye, Trash2, CheckCircle2, Award, RotateCcw, ChevronRight, ArrowLeft, TrendingUp, Trophy, Activity, History, Timer, FileCheck2, XCircle, Settings as SettingsIcon, Palmtree, LogOut, ShieldAlert, BellRing, Send, ClipboardCheck, ClipboardList } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { RecruitmentSettingsPanel, AgentSessionsPanel } from "./AreaComando";
import HRFormazionePanel from "../components/HRFormazionePanel";
import AssessmentAdminPanel from "../components/AssessmentAdminPanel";
import { useAuth } from "../context/AuthContext";

const STATUS_META = {
  pending: { label: "In Sospeso", color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  accepted: { label: "Accettata", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  rejected: { label: "Rifiutata", color: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};

const STATUS_ACTION_LABEL = {
  accepted: "Accetta candidatura",
  rejected: "Rifiuta candidatura",
  pending: "Lascia in Sospeso",
};

const HR_VIEWS = {
  HOME: "home",
  ESAMI: "esami",              // Unificato: richieste + esami allievi + esami agenti + bandi agenti
  RECLUTAMENTO: "reclutamento", // Unificato: candidature + bandi pubblici
  SERVICE_HOURS: "service_hours",
  FORMAZIONE: "formazione",
  ASSESSMENT: "assessment",     // Quiz Valutazione (grado potenziale)
  REPORT_CORRECTIONS: "report_corrections",  // Correzione Rapporti RP → notifica Discord
};

export default function AreaHR() {
  const [view, setView] = useState(HR_VIEWS.HOME);
  const goHome = useCallback(() => setView(HR_VIEWS.HOME), []);
  const { user } = useAuth();
  const isViewer = !!user?.is_viewer;

  const content = (() => {
    if (view === HR_VIEWS.HOME) {
      return (
        <HRHomePanels
          onOpenReclutamento={() => setView(HR_VIEWS.RECLUTAMENTO)}
          onOpenEsami={() => setView(HR_VIEWS.ESAMI)}
          onOpenServiceHours={() => setView(HR_VIEWS.SERVICE_HOURS)}
          onOpenFormazione={() => setView(HR_VIEWS.FORMAZIONE)}
          onOpenAssessment={() => setView(HR_VIEWS.ASSESSMENT)}
          onOpenReportCorrections={() => setView(HR_VIEWS.REPORT_CORRECTIONS)}
        />
      );
    }
    if (view === HR_VIEWS.RECLUTAMENTO) return <HRBackWrap onBack={goHome} title="Reclutamento" icon={Users}><ReclutamentoUnified /></HRBackWrap>;
    if (view === HR_VIEWS.ESAMI) return <HRBackWrap onBack={goHome} title="Esami" icon={GraduationCap}><EsamiUnified /></HRBackWrap>;
    if (view === HR_VIEWS.SERVICE_HOURS) return <HRBackWrap onBack={goHome} title="Gestione Personale" icon={Users}><ServiceHoursPanel /></HRBackWrap>;
    if (view === HR_VIEWS.FORMAZIONE) return <HRBackWrap onBack={goHome} title="Formazione Allievi" icon={GraduationCap}><HRFormazionePanel /></HRBackWrap>;
    if (view === HR_VIEWS.ASSESSMENT) return <HRBackWrap onBack={goHome} title="Quiz Valutazione" icon={ClipboardList}><AssessmentAdminPanel /></HRBackWrap>;
    if (view === HR_VIEWS.REPORT_CORRECTIONS) return <HRBackWrap onBack={goHome} title="Correzione Rapporti" icon={FileCheck2}><ReportCorrectionsPanel /></HRBackWrap>;
    return null;
  })();

  if (!isViewer) return content;

  // Modalità VISUALIZZATORE: banner + disabilita azioni tramite CSS
  return (
    <div className="space-y-4" data-testid="area-hr-viewer-mode">
      <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-300 shrink-0 mt-0.5" />
        <div>
          <div className="text-sm font-bold text-amber-200">Modalità Sola Lettura</div>
          <p className="text-xs text-amber-200/80 leading-relaxed mt-1">
            Il tuo grado è <span className="font-semibold">Visualizzatore</span>: puoi navigare
            e consultare tutti i dati dell&apos;Area HR, ma qualsiasi azione (accetta, rifiuta,
            promuovi, sanziona, modifica ferie/dimissioni, formazione, ecc.) è disabilitata.
          </p>
        </div>
      </div>
      <div
        className="hr-viewer-shield"
        data-testid="hr-viewer-shield"
        style={{ position: "relative" }}
      >
        {content}
      </div>
      <style>{`
        /* Disabilita fisicamente tutti gli input di scrittura nell'area HR quando in modalità viewer.
           I bottoni "back" e "refresh" (data-testid con -back o -refresh) restano attivi. */
        .hr-viewer-shield button:not([data-testid*="back"]):not([data-testid*="refresh"]):not([data-testid^="tab-"]):not([data-testid^="hr-panel-"]):not([role="tab"]),
        .hr-viewer-shield input:not([type="search"]):not([type="hidden"]),
        .hr-viewer-shield textarea,
        .hr-viewer-shield select,
        .hr-viewer-shield [role="combobox"],
        .hr-viewer-shield [role="switch"] {
          pointer-events: none !important;
          opacity: 0.55 !important;
          cursor: not-allowed !important;
          filter: grayscale(0.3);
        }
        /* Consenti ancora hover/click sui link (Eye) di dettaglio in modalità viewer */
        .hr-viewer-shield a[data-testid*="view"],
        .hr-viewer-shield button[data-testid*="view"],
        .hr-viewer-shield button[data-testid*="detail"],
        .hr-viewer-shield button[data-testid*="open"] {
          pointer-events: auto !important;
          opacity: 1 !important;
          filter: none !important;
          cursor: pointer !important;
        }
      `}</style>
    </div>
  );
}

// ------------------------------------------------------------
// Wrapper unificati (esami + reclutamento) con tab interni
// ------------------------------------------------------------
function EsamiUnified() {
  return (
    <div className="space-y-4" data-testid="esami-unified">
      <Tabs defaultValue="richieste" className="w-full">
        <TabsList className="bg-[#143830] border border-sheriff-gold/25 rounded-xl flex-wrap h-auto">
          <TabsTrigger value="richieste" data-testid="tab-esami-richieste" className="rounded-xl uppercase-label">
            <FileCheck2 className="h-4 w-4 mr-2" /> Richieste Esami
          </TabsTrigger>
          <TabsTrigger value="allievi" data-testid="tab-esami-allievi" className="rounded-xl uppercase-label">
            <GraduationCap className="h-4 w-4 mr-2" /> Esami Allievi
          </TabsTrigger>
          <TabsTrigger value="agenti" data-testid="tab-esami-agenti" className="rounded-xl uppercase-label">
            <TrendingUp className="h-4 w-4 mr-2" /> Esami Agenti
          </TabsTrigger>
          <TabsTrigger value="bandi" data-testid="tab-esami-bandi" className="rounded-xl uppercase-label">
            <SettingsIcon className="h-4 w-4 mr-2" /> Bandi Agenti
          </TabsTrigger>
        </TabsList>
        <TabsContent value="richieste" className="mt-6"><ExamAuthRequestsPanel /></TabsContent>
        <TabsContent value="allievi" className="mt-6"><ExamResultsPanel /></TabsContent>
        <TabsContent value="agenti" className="mt-6"><AgentExamsPanel /></TabsContent>
        <TabsContent value="bandi" className="mt-6"><AgentSessionsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReclutamentoUnified() {
  return (
    <div className="space-y-4" data-testid="reclutamento-unified">
      <Tabs defaultValue="candidature" className="w-full">
        <TabsList className="bg-[#143830] border border-sheriff-gold/25 rounded-xl flex-wrap h-auto">
          <TabsTrigger value="candidature" data-testid="tab-recruit-candidature" className="rounded-xl uppercase-label">
            <Users className="h-4 w-4 mr-2" /> Candidature
          </TabsTrigger>
          <TabsTrigger value="bandi" data-testid="tab-recruit-bandi" className="rounded-xl uppercase-label">
            <SettingsIcon className="h-4 w-4 mr-2" /> Bandi Pubblici
          </TabsTrigger>
        </TabsList>
        <TabsContent value="candidature" className="mt-6"><CandidaturesPanel /></TabsContent>
        <TabsContent value="bandi" className="mt-6"><RecruitmentSettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function HRBackWrap({ onBack, title, icon: Icon, children }) {
  return (
    <div className="space-y-6" data-testid="area-hr">
      <div className="flex items-center gap-3">
        <Button
          onClick={onBack}
          variant="outline"
          size="sm"
          data-testid="back-to-hr-home"
          className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Indietro
        </Button>
        <div className="flex items-center gap-2 text-sheriff-gold">
          <Icon className="h-5 w-5" />
          <span className="font-heading font-bold text-xl text-[#F5E6B8]">{title}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

function HRHomePanels({ onOpenReclutamento, onOpenEsami, onOpenServiceHours, onOpenFormazione, onOpenAssessment, onOpenReportCorrections }) {
  const [counts, setCounts] = useState({
    pending: null,
    promotionReadyAllievi: null,
    promotionReadyAgenti: null,
    openSessions: null,
    activeShifts: null,
    pendingExamAuth: null,
    assessmentAttempts: null,
  });

  useEffect(() => {
    Promise.all([
      api.get("/candidatures").catch(() => ({ data: [] })),
      api.get("/quiz/admin/promotion-ready").catch(() => ({ data: [] })),
      api.get("/quiz/admin/sessions").catch(() => ({ data: [] })),
      api.get("/shifts/active").catch(() => ({ data: { count: 0 } })),
      api.get("/exam-auth/admin", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      api.get("/assessment/attempts").catch(() => ({ data: { items: [] } })),
      api.get("/assessment/auth/admin", { params: { status: "pending" } }).catch(() => ({ data: { items: [] } })),
    ]).then(([cands, promo, sess, active, examAuth, assessment, assessAuth]) => {
      const pending = (cands.data || []).filter((c) => c.status === "pending").length;
      const openSessions = (sess.data || []).filter((s) => s.open).length;
      const promoList = promo.data || [];
      const allievi = promoList.filter((p) => (p.exam_tier || "vice_sceriffo") === "vice_sceriffo").length;
      const agenti = promoList.filter((p) => ["sergente", "tenente", "capitano"].includes(p.exam_tier)).length;
      setCounts({
        pending,
        promotionReadyAllievi: allievi,
        promotionReadyAgenti: agenti,
        openSessions,
        activeShifts: active.data?.count ?? 0,
        pendingExamAuth: (examAuth.data || []).length,
        assessmentAttempts: (assessment.data?.items || []).length,
        assessmentAuthPending: (assessAuth.data?.items || []).length,
      });
    });
  }, []);

  const totalExamsPending = (counts.pendingExamAuth || 0) + (counts.promotionReadyAllievi || 0) + (counts.promotionReadyAgenti || 0);

  return (
    <div className="space-y-8" data-testid="area-hr">
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <Users className="h-4 w-4" /> Risorse Umane
        </div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#F5E6B8] mb-2">
          Personnel Administration Bureau
        </h1>
        <p className="text-[#D4C480] max-w-2xl">
          Gestisci reclutamento, esami di promozione, formazione degli Allievi e gestione operativa del personale.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-5">
        <HRPanelCard
          highlight={counts.pending > 0}
          icon={Users}
          title="Reclutamento"
          subtitle="Candidature + Bandi Pubblici"
          desc="Valuta le candidature ricevute, aggiungi note ai candidati e apri/chiudi i bandi pubblici di reclutamento in un unico pannello."
          badge={counts.pending}
          badgeLabel={counts.pending === 1 ? "candidatura in sospeso" : "candidature in sospeso"}
          badgeTone={counts.pending > 0 ? "amber" : "gold"}
          ctaLabel="Apri Reclutamento"
          onClick={onOpenReclutamento}
          testId="hr-panel-reclutamento"
        />
        <HRPanelCard
          highlight={totalExamsPending > 0}
          icon={GraduationCap}
          title="Esami"
          subtitle="Richieste · Allievi · Agenti · Bandi"
          desc="Pannello unificato per esami: autorizza richieste, valuta i tentativi degli Allievi, gestisci le classifiche di promozione e i bandi per Sergente/Tenente/Capitano."
          badge={totalExamsPending}
          badgeLabel={totalExamsPending === 1 ? "azione richiesta" : "azioni richieste"}
          badgeTone={totalExamsPending > 0 ? "gold" : "gold"}
          ctaLabel="Apri Esami"
          onClick={onOpenEsami}
          testId="hr-panel-esami"
        />
        <HRPanelCard
          highlight={counts.activeShifts > 0}
          icon={Users}
          title="Gestione Personale"
          subtitle="Personale · Ferie · Sanzioni · Dimissioni · Ore"
          desc="Pannello centralizzato di gestione HR: personale attivo, ferie approvate, richieste dimissioni, sanzioni disciplinari e turni operativi in tempo reale."
          badge={counts.activeShifts}
          badgeLabel={counts.activeShifts === 1 ? "in servizio" : "in servizio"}
          badgeTone={counts.activeShifts > 0 ? "emerald" : "gold"}
          ctaLabel="Apri Pannello"
          onClick={onOpenServiceHours}
          testId="hr-panel-service-hours"
        />
        <HRPanelCard
          icon={GraduationCap}
          title="Formazione Allievi"
          subtitle="Percorsi formativi"
          desc="Gestisci il percorso formativo degli Allievi Vice Sceriffo: aggiorna gli step (colloqui, corsi, esami), aggiungi note e monitora l'avanzamento verso la promozione."
          ctaLabel="Apri Pannello"
          onClick={onOpenFormazione}
          testId="hr-panel-formazione"
        />
        <HRPanelCard
          icon={ClipboardList}
          title="Quiz Valutazione"
          subtitle="Autorizzazioni · Tentativi · Domande"
          desc="Approva le richieste di quiz degli operatori, consulta i tentativi con grado potenziale suggerito e gestisci le domande del Quiz Attitudinale."
          highlight={counts.assessmentAuthPending > 0}
          badge={counts.assessmentAuthPending || counts.assessmentAttempts}
          badgeLabel={
            counts.assessmentAuthPending > 0
              ? (counts.assessmentAuthPending === 1 ? "richiesta in attesa" : "richieste in attesa")
              : (counts.assessmentAttempts === 1 ? "tentativo" : "tentativi")
          }
          badgeTone={counts.assessmentAuthPending > 0 ? "amber" : "gold"}
          ctaLabel="Apri Quiz Valutazione"
          onClick={onOpenAssessment}
          testId="hr-panel-assessment"
        />
        <HRPanelCard
          icon={FileCheck2}
          title="Correzione Rapporti"
          subtitle="Notifica correzione rapporto RP"
          desc="Segnala all'operatore che il suo rapporto necessita correzioni. Compila numero rapporto e seleziona il destinatario: il bot invierà una notifica su Discord con l'invito ad aprire un ticket al Personnel Administration Bureau."
          ctaLabel="Apri Pannello"
          onClick={onOpenReportCorrections}
          testId="hr-panel-report-corrections"
        />
      </div>
    </div>
  );
}

function HRPanelCard({ icon: Icon, title, subtitle, desc, ctaLabel, onClick, testId, highlight, badge, badgeLabel, badgeTone }) {
  const baseCls = "relative overflow-hidden rounded-2xl border p-6 flex flex-col group transition-all hover:-translate-y-0.5 hover:shadow-2xl";
  const cls = highlight
    ? `${baseCls} border-sheriff-gold/60 bg-gradient-to-br from-sheriff-gold/20 via-[#143830] to-[#0F2620] hover:border-sheriff-gold shadow-lg shadow-sheriff-gold/10`
    : `${baseCls} border-sheriff-gold/25 bg-gradient-to-br from-[#143830] to-[#0F2620] hover:border-sheriff-gold/60`;
  const badgeCls = badgeTone === "gold"
    ? "bg-sheriff-gold/20 text-sheriff-gold border-sheriff-gold/50"
    : badgeTone === "amber"
      ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
      : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  return (
    <div data-testid={testId} className={cls}>
      {highlight && (
        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-sheriff-gold/15 blur-3xl pointer-events-none" />
      )}
      <div className="relative flex items-start gap-3 mb-3">
        <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 border ${highlight ? "border-sheriff-gold/60 bg-sheriff-gold/20" : "border-sheriff-gold/30 bg-sheriff-gold/10"}`}>
          <Icon className="h-6 w-6 text-sheriff-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="uppercase-label text-sheriff-gold/80 text-[10px] mb-0.5">{subtitle}</div>
          <h3 className="font-heading font-black text-2xl text-[#F5E6B8] tracking-tighter">{title}</h3>
        </div>
        {badge != null && badge > 0 && (
          <span className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.12em] font-bold ${badgeCls}`} data-testid={`${testId}-badge`}>
            <span className="font-mono text-sm">{badge}</span>
            <span>{badgeLabel}</span>
          </span>
        )}
      </div>
      <p className="text-sm text-[#D4C480] mb-5 leading-relaxed relative">{desc}</p>
      <Button
        onClick={onClick}
        data-testid={`${testId}-cta`}
        className={`mt-auto rounded-full uppercase-label font-bold ${
          highlight
            ? "bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18]"
            : "bg-sheriff-gold/15 hover:bg-sheriff-gold/25 text-sheriff-gold border border-sheriff-gold/40"
        }`}
      >
        {ctaLabel} <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    </div>
  );
}

function CandidaturesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const reloadCandidatures = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/candidatures");
      setItems(data);
    } catch {
      toast.error("Impossibile caricare le candidature");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/candidatures");
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) toast.error("Impossibile caricare le candidature");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openNoteDialog = (id, status, currentNote) => {
    setNoteDialog({ id, status, currentNote });
    setNoteText(currentNote || "");
  };

  const confirmStatusChange = async () => {
    if (!noteDialog) return;
    setSaving(true);
    try {
      const payload = { status: noteDialog.status, note: noteText.trim() || null };
      await api.patch(`/candidatures/${noteDialog.id}`, payload);
      setItems((prev) => prev.map((c) => (c.id === noteDialog.id
        ? { ...c, status: noteDialog.status, hr_note: payload.note }
        : c)));
      toast.success(`Stato aggiornato: ${STATUS_META[noteDialog.status].label}`);
      setNoteDialog(null);
      setNoteText("");
    } catch {
      toast.error("Errore aggiornamento stato");
    } finally {
      setSaving(false);
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = {
    all: items.length,
    pending: items.filter((i) => i.status === "pending").length,
    accepted: items.filter((i) => i.status === "accepted").length,
    rejected: items.filter((i) => i.status === "rejected").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        {[
          { k: "all", label: "Tutte" },
          { k: "pending", label: "In Sospeso" },
          { k: "accepted", label: "Accettate" },
          { k: "rejected", label: "Rifiutate" },
        ].map((b) => (
          <Button
            key={b.k}
            variant={filter === b.k ? "default" : "outline"}
            size="sm"
            data-testid={`hr-filter-${b.k}`}
            className={`rounded-xl uppercase-label ${filter === b.k ? "bg-sheriff-green text-white hover:bg-sheriff-green-dark" : ""}`}
            onClick={() => setFilter(b.k)}
          >
            {b.label} <span className="ml-1.5 opacity-70 font-mono">({counts[b.k]})</span>
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="bg-[#143830] border border-sheriff-gold/25 p-12 rounded-xl text-center">
          <Loader2 className="h-8 w-8 animate-spin text-sheriff-gold mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#143830] border border-sheriff-gold/25 p-12 rounded-xl text-center text-[#B8A269]" data-testid="hr-empty">
          Nessuna candidatura in questa categoria.
        </div>
      ) : (
        <div className="grid gap-4" data-testid="hr-candidatures-list">
          {filtered.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.pending;
            return (
              <div key={c.id} data-testid={`candidature-${c.id}`} className="bg-[#143830] border border-sheriff-gold/25 rounded-xl p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="font-heading font-bold text-xl text-[#F5E6B8]">{c.rp_name}</div>
                    <div className="font-mono-codes text-xs text-[#B8A269] mt-1">
                      Inviata il {new Date(c.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                  <Badge className={`${meta.color} border rounded-xl uppercase-label`} data-testid={`status-badge-${c.id}`}>
                    {c.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                    {meta.label}
                  </Badge>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                  <Info label="Data di nascita RP" value={c.rp_dob} />
                  <Info label="Telefono RP" value={c.phone_rp || "—"} mono />
                  <Info label="E-Mail RP" value={c.email_rp || "—"} mono />
                  <Info label="Nickname" value={c.nickname} />
                  <Info label="ID Discord" value={c.discord_id} mono />
                  <Info label="Codice Tracciamento" value={c.tracking_code || "—"} mono />
                  <Info label="Disponibilità" value={c.availability} />
                </div>
                {c.past_experience && (
                  <div className="mt-4 p-4 bg-[#0A1F18] border border-sheriff-gold/15 rounded-xl">
                    <div className="uppercase-label text-sheriff-gold mb-2">Esperienze Passate</div>
                    <p className="text-sm text-[#E8D89A] whitespace-pre-wrap leading-relaxed">{c.past_experience}</p>
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-sheriff-gold/15 flex flex-wrap gap-2">
                  <Button
                    data-testid={`accept-btn-${c.id}`}
                    onClick={() => openNoteDialog(c.id, "accepted", c.hr_note)}
                    disabled={c.status === "accepted"}
                    className="rounded-xl uppercase-label bg-emerald-600 hover:bg-emerald-700 text-white"
                    size="sm"
                  >
                    <Check className="h-4 w-4 mr-1.5" /> Accetta
                  </Button>
                  <Button
                    data-testid={`reject-btn-${c.id}`}
                    onClick={() => openNoteDialog(c.id, "rejected", c.hr_note)}
                    disabled={c.status === "rejected"}
                    className="rounded-xl uppercase-label bg-rose-600 hover:bg-rose-700 text-white"
                    size="sm"
                  >
                    <X className="h-4 w-4 mr-1.5" /> Rifiuta
                  </Button>
                  <Button
                    data-testid={`pending-btn-${c.id}`}
                    onClick={() => openNoteDialog(c.id, "pending", c.hr_note)}
                    disabled={c.status === "pending"}
                    variant="outline"
                    className="rounded-xl uppercase-label border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                    size="sm"
                  >
                    <Clock className="h-4 w-4 mr-1.5" /> Lascia in Sospeso
                  </Button>
                  <Button
                    data-testid={`note-btn-${c.id}`}
                    onClick={() => openNoteDialog(c.id, c.status, c.hr_note)}
                    variant="outline"
                    className="rounded-xl uppercase-label border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 ml-auto"
                    size="sm"
                  >
                    <StickyNote className="h-4 w-4 mr-1.5" /> {c.hr_note ? "Modifica Nota" : "Aggiungi Nota"}
                  </Button>
                </div>

                {c.hr_note && (
                  <div className="mt-4 p-4 bg-sheriff-gold/8 border border-sheriff-gold/30 rounded-xl" data-testid={`hr-note-display-${c.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquareText className="h-4 w-4 text-sheriff-gold" />
                      <span className="uppercase-label text-sheriff-gold">Nota Risorse Umane</span>
                      {c.reviewed_by && (
                        <span className="text-xs text-[#B8A269] font-mono ml-auto">
                          {c.reviewed_by_role && <span className="text-sheriff-gold font-bold">{c.reviewed_by_role} · </span>}
                          {c.reviewed_by}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#E8D89A] whitespace-pre-wrap leading-relaxed">{c.hr_note}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!noteDialog} onOpenChange={(o) => { if (!o) { setNoteDialog(null); setNoteText(""); } }}>
        <DialogContent className="max-w-lg bg-[#143830] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl" data-testid="hr-note-dialog">
          <DialogHeader>
            <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
              <MessageSquareText className="h-4 w-4" /> {noteDialog ? STATUS_ACTION_LABEL[noteDialog.status] : ""}
            </div>
            <DialogTitle className="font-heading text-2xl text-[#F5E6B8]">Lascia una nota (opzionale)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#D4C480]">
              Questa nota verrà <span className="font-bold text-sheriff-gold">mostrata al candidato</span> nella pagina di tracciamento della sua candidatura.
            </p>
            <Textarea
              data-testid="hr-note-textarea"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={5}
              placeholder="Es. Ci congratuliamo per la tua candidatura. Ti contatteremo presto per i prossimi step…"
              className="bg-[#0A1F18] border-sheriff-gold/30 rounded-xl text-[#F5E6B8] placeholder:text-[#8B7B49]"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setNoteDialog(null); setNoteText(""); }}
              className="border-sheriff-gold/30 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label"
              data-testid="hr-note-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmStatusChange}
              disabled={saving}
              className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] rounded-full uppercase-label font-bold"
              data-testid="hr-note-confirm"
            >
              {saving ? "Salvataggio…" : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// ESAMI ALLIEVI — risultati del questionario teorico
// ============================================================
function ExamResultsPanel() {
  const [items, setItems] = useState([]);
  const [promotionReady, setPromotionReady] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);

  const reloadExams = async () => {
    setLoading(true);
    try {
      const [attempts, promo] = await Promise.all([
        api.get("/quiz/admin/attempts", { params: { scope: "allievi" } }),
        api.get("/quiz/admin/promotion-ready"),
      ]);
      setItems(attempts.data);
      // promotion-ready filtra solo allievi: lo facciamo lato client
      setPromotionReady((promo.data || []).filter((p) => (p.exam_tier || "vice_sceriffo") === "vice_sceriffo"));
    } catch {
      toast.error("Errore caricamento esami allievi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [attempts, promo] = await Promise.all([
          api.get("/quiz/admin/attempts", { params: { scope: "allievi" } }),
          api.get("/quiz/admin/promotion-ready"),
        ]);
        if (cancelled) return;
        setItems(attempts.data);
        setPromotionReady((promo.data || []).filter((p) => (p.exam_tier || "vice_sceriffo") === "vice_sceriffo"));
      } catch {
        if (!cancelled) toast.error("Errore caricamento esami allievi");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/quiz/admin/attempts/${id}`);
      setDetail(data);
    } catch {
      toast.error("Errore caricamento dettaglio");
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo esame?")) return;
    try {
      await api.delete(`/quiz/admin/attempts/${id}`);
      setItems((prev) => prev.filter((a) => a.id !== id));
      // Ricarica anche promotion-ready perché potrebbe cambiare
      try {
        const { data } = await api.get("/quiz/admin/promotion-ready");
        setPromotionReady(data);
      } catch (err) {
        // Non bloccante: lascia il banner precedente, il prossimo refresh lo correggerà
        console.warn("[AreaHR] promotion-ready refresh failed after delete:", err?.message || err);
      }
      toast.success("Esame eliminato");
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const filtered = filter === "all"
    ? items
    : filter === "passed"
      ? items.filter((a) => a.passed)
      : items.filter((a) => !a.passed);

  const counts = {
    all: items.length,
    passed: items.filter((a) => a.passed).length,
    failed: items.filter((a) => !a.passed).length,
  };

  return (
    <div className="space-y-6">
      {/* BANNER ALLIEVI PRONTI PER PROMOZIONE */}
      {promotionReady.length > 0 && (
        <PromotionReadyBanner items={promotionReady} onPromoted={reloadExams} />
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Esami Totali" value={items.length} accent="text-sheriff-gold" />
        <StatCard label="Superati" value={counts.passed} accent="text-emerald-300" />
        <StatCard label="Bocciati" value={counts.failed} accent="text-rose-300" />
        <StatCard label="Pronti per Promozione" value={promotionReady.length} accent="text-sheriff-gold" sub="Allievi che hanno superato" />
      </div>

      {/* Filtri + azioni */}
      <div className="flex gap-2 flex-wrap items-center">
        {[
          { k: "all", label: "Tutti" },
          { k: "passed", label: "Superati" },
          { k: "failed", label: "Bocciati" },
        ].map((b) => (
          <Button
            key={b.k}
            variant={filter === b.k ? "default" : "outline"}
            size="sm"
            data-testid={`exam-filter-${b.k}`}
            className={`rounded-xl uppercase-label ${filter === b.k ? "bg-sheriff-green text-white hover:bg-sheriff-green-dark" : ""}`}
            onClick={() => setFilter(b.k)}
          >
            {b.label} <span className="ml-1.5 opacity-70 font-mono">({counts[b.k]})</span>
          </Button>
        ))}
        <div className="ml-auto">
          <ResetRateLimitButton onDone={reloadExams} />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-10 text-center">
          <GraduationCap className="h-10 w-10 text-sheriff-gold/50 mx-auto mb-3" />
          <p className="text-[#B8A269]">Nessun esame ancora registrato in questa categoria.</p>
        </div>
      ) : (
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-sheriff-gold/15">
                <TableHead className="text-sheriff-gold">Candidato</TableHead>
                <TableHead className="text-sheriff-gold">Esame</TableHead>
                <TableHead className="text-sheriff-gold">Data</TableHead>
                <TableHead className="text-sheriff-gold text-center">Punteggio</TableHead>
                <TableHead className="text-sheriff-gold text-center">Esito</TableHead>
                <TableHead className="text-sheriff-gold text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((a, i) => {
                const tierLabels = { vice_sceriffo: "Vice Sceriffo", sergente: "Sergente", tenente: "Tenente", capitano: "Capitano" };
                const tierLabel = tierLabels[a.exam_tier || "vice_sceriffo"];
                return (
                <TableRow
                  key={a.id}
                  className="hover:bg-sheriff-gold/5 cursor-pointer border-sheriff-gold/10"
                  onClick={() => openDetail(a.id)}
                  data-testid={`exam-attempt-row-${i}`}
                >
                  <TableCell className="text-[#F5E6B8] font-semibold">
                    <div>{a.user_rp_name || "—"}</div>
                    <div className="text-[10px] text-[#8B7B49] font-mono mt-0.5">{a.user_email}</div>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-bold border bg-sheriff-gold/10 border-sheriff-gold/30 text-sheriff-gold">
                      {tierLabel}
                    </span>
                  </TableCell>
                  <TableCell className="text-[#D4C480] font-mono text-xs whitespace-nowrap">{new Date(a.submitted_at).toLocaleString("it-IT")}</TableCell>
                  <TableCell className="text-center font-mono font-bold text-sheriff-gold whitespace-nowrap">{a.correct_count}/{a.total} · {a.score_pct}%</TableCell>
                  <TableCell className="text-center">
                    <span className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-bold border ${a.passed ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`}>
                      {a.passed ? "Superato" : "Bocciato"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDetail(a.id); }} className="border-sheriff-gold/40 text-sheriff-gold rounded-full h-7 w-7 p-0" data-testid={`exam-view-${i}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={(e) => handleDelete(a.id, e)} className="border-rose-500/40 text-rose-300 rounded-full h-7 w-7 p-0" data-testid={`exam-delete-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="exam-attempt-detail">
          {detail && (
            <>
              <DialogHeader>
                <div className="uppercase-label text-sheriff-gold mb-1">Dettaglio Esame Allievo</div>
                <DialogTitle className="font-heading font-black text-2xl text-[#F5E6B8]">
                  {detail.user_rp_name}
                  <span className={`ml-3 font-mono ${detail.passed ? "text-emerald-300" : "text-rose-300"}`}>
                    {detail.correct_count}/{detail.total} · {detail.score_pct}%
                  </span>
                </DialogTitle>
                <div className="text-xs text-[#B8A269] font-mono mt-1">{new Date(detail.submitted_at).toLocaleString("it-IT")}</div>
              </DialogHeader>
              <div className="space-y-3 pt-2">
                {detail.details.map((d, i) => {
                  const isOpen = d.type === "open";
                  const grade = d.manual_grade; // true / false / null
                  let borderCls;
                  if (isOpen) {
                    if (grade === true) borderCls = "border-emerald-500/40 bg-emerald-500/5";
                    else if (grade === false) borderCls = "border-rose-500/40 bg-rose-500/5";
                    else borderCls = "border-sheriff-gold/40 bg-sheriff-gold/5";
                  } else {
                    borderCls = d.is_correct ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5";
                  }
                  let icon;
                  if (isOpen) {
                    if (grade === true) icon = <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />;
                    else if (grade === false) icon = <X className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />;
                    else icon = <MessageSquareText className="h-5 w-5 text-sheriff-gold mt-0.5 shrink-0" />;
                  } else {
                    icon = d.is_correct ? <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" /> : <X className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />;
                  }
                  return (
                    <div key={d.question_id} className={`p-4 rounded-xl border ${borderCls}`}>
                      <div className="flex items-start gap-2">
                        {icon}
                        <div className="flex-1">
                          <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60 mb-1 flex items-center gap-2 flex-wrap">
                            <span>Domanda {i + 1}</span>
                            {d.category && <span className="text-[#B8A269]">· {d.category}</span>}
                            {isOpen && (
                              <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                                grade === true ? "bg-emerald-500/15 text-emerald-300" :
                                grade === false ? "bg-rose-500/15 text-rose-300" :
                                "bg-sheriff-gold/15 text-sheriff-gold"
                              }`}>
                                APERTA · {grade === true ? "CORRETTA" : grade === false ? "SBAGLIATA" : "DA VALUTARE"}
                              </span>
                            )}
                          </div>
                          <div className="font-semibold text-[#F5E6B8] mb-2">{d.text}</div>
                          {isOpen ? (
                            <>
                              <div className="bg-[#0A1F18] border border-sheriff-gold/20 rounded-lg p-3 mt-1">
                                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-sheriff-gold/70 mb-1">Risposta dell&apos;allievo</div>
                                <p className="text-sm text-[#E8D89A] leading-relaxed whitespace-pre-wrap">
                                  {d.text_answer?.trim() ? d.text_answer : <span className="italic text-[#8B7B49]">— nessuna risposta fornita —</span>}
                                </p>
                                {d.explanation && (
                                  <div className="mt-3 pt-3 border-t border-sheriff-gold/15">
                                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-sheriff-gold/70 mb-1">Nota interna</div>
                                    <p className="text-xs text-[#8B7B49] italic">{d.explanation}</p>
                                  </div>
                                )}
                              </div>
                              <OpenGradingButtons
                                attemptId={detail.id}
                                questionId={d.question_id}
                                currentGrade={grade}
                                onGraded={(updated) => {
                                  setDetail(updated);
                                  reloadExams(); // ricarica lista per aggiornare punteggio/promo
                                }}
                              />
                              {d.graded_by && (
                                <div className="mt-2 text-[10px] text-sheriff-gold/60 font-mono">
                                  Valutato da {d.graded_by}{d.graded_at ? ` · ${new Date(d.graded_at).toLocaleString("it-IT")}` : ""}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="text-xs text-[#B8A269]">
                                Risposta data:{" "}
                                <span className="font-mono text-[#E8D89A]">
                                  {d.selected_index != null ? `${String.fromCharCode(65 + d.selected_index)}) ${d.options[d.selected_index]}` : "— nessuna —"}
                                </span>
                              </div>
                              <div className="text-xs text-emerald-300 mt-0.5">
                                Risposta corretta: <span className="font-mono">{String.fromCharCode(65 + d.correct_index)}) {d.options[d.correct_index]}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ResetRateLimitButton({ onDone }) {
  const [loading, setLoading] = useState(false);
  const handleReset = async () => {
    if (!window.confirm(
      "Confermi l'azzeramento del limite di 1 esame/24h per TUTTI gli allievi?\n\n" +
      "I tentativi precedenti restano visibili in questa pagina, ma da ora ogni allievo può sostenere subito un nuovo esame. " +
      "Il limite di 24h riparte normalmente dal prossimo tentativo."
    )) return;
    setLoading(true);
    try {
      const { data } = await api.post("/quiz/admin/reset-rate-limit");
      toast.success(data?.message || "Limite giornaliero azzerato per tutti gli allievi");
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore reset limite");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      onClick={handleReset}
      disabled={loading}
      size="sm"
      variant="outline"
      data-testid="reset-rate-limit-btn"
      className="rounded-xl uppercase-label border-sheriff-gold/50 text-sheriff-gold hover:bg-sheriff-gold/10"
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <RotateCcw className="h-3.5 w-3.5 mr-2" />}
      Reset Limite 24h
    </Button>
  );
}

function ResetAgentCooldownButton({ tier, tierLabel, onDone }) {
  const [loading, setLoading] = useState(false);
  const handleReset = async () => {
    if (!window.confirm(
      `Confermi l'azzeramento del cooldown di 120h (5 giorni) per il bando ${tierLabel}?\n\n` +
      "Tutti gli agenti idonei potranno sostenere subito un nuovo esame, anche se hanno tentato negli ultimi 5 giorni. " +
      "I tentativi precedenti restano visibili nello storico HR.\n\n" +
      "Il cooldown di 120h riparte normalmente dal prossimo tentativo."
    )) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/quiz/admin/reset-agent-cooldown/${tier}`);
      toast.success(data?.message || `Cooldown 120h azzerato per ${tierLabel}`);
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore reset cooldown");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      onClick={handleReset}
      disabled={loading}
      size="sm"
      variant="outline"
      data-testid={`reset-agent-cooldown-btn-${tier}`}
      className="rounded-xl uppercase-label border-sheriff-gold/50 text-sheriff-gold hover:bg-sheriff-gold/10"
    >
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <RotateCcw className="h-3.5 w-3.5 mr-2" />}
      Reset Cooldown 120h
    </Button>
  );
}

function StatCard({ label, value, accent = "text-sheriff-gold", sub }) {
  return (
    <div className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-4">
      <div className="uppercase-label text-[#B8A269] text-[10px] mb-1">{label}</div>
      <div className={`font-heading font-black text-3xl ${accent}`}>{value}</div>
      {sub && <div className="text-[10px] text-[#8B7B49] mt-1">{sub}</div>}
    </div>
  );
}

function PromotionReadyBanner({ items, onPromoted }) {
  const [promotingId, setPromotingId] = useState(null);

  const handlePromote = async (item) => {
    const target = item.next_role || "Vice Sceriffo";
    if (!window.confirm(`Confermi la promozione di ${item.rp_name || item.email} a ${target}?`)) return;
    setPromotingId(item.user_id);
    try {
      await api.post(`/quiz/admin/promote/${item.user_id}`);
      toast.success(`${item.rp_name || item.email} promosso a ${target}`);
      onPromoted?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore promozione";
      toast.error(typeof detail === "string" ? detail : "Errore promozione");
    } finally {
      setPromotingId(null);
    }
  };

  const count = items.length;
  return (
    <div
      data-testid="promotion-ready-banner"
      className="relative overflow-hidden rounded-2xl border-2 border-sheriff-gold/60 bg-gradient-to-br from-sheriff-gold/20 via-[#143830] to-[#0F2620] p-5 sm:p-6"
    >
      {/* Decorative glow */}
      <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-sheriff-gold/15 blur-3xl pointer-events-none" />

      <div className="flex flex-col sm:flex-row sm:items-start gap-4 relative">
        <div className="h-14 w-14 rounded-2xl bg-sheriff-gold/25 border border-sheriff-gold/60 flex items-center justify-center shrink-0">
          <Award className="h-7 w-7 text-sheriff-gold" />
        </div>
        <div className="flex-1">
          <div className="uppercase-label text-sheriff-gold mb-1">Azione Richiesta · Promozioni</div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-[#F5E6B8] tracking-tighter">
            {count === 1
              ? "1 utente pronto per la promozione"
              : `${count} utenti pronti per la promozione`}
          </h3>
          <p className="text-sm text-[#D4C480] mt-2">
            Questi utenti hanno superato l&apos;esame del loro tier. Premi il pulsante qui sotto per promuoverli direttamente al grado successivo.
          </p>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 relative">
        {items.map((p) => {
          const isPromoting = promotingId === p.user_id;
          const targetRole = p.next_role || "Vice Sceriffo";
          return (
            <div
              key={`${p.user_id}-${p.exam_tier || "vice_sceriffo"}`}
              data-testid={`promotion-card-${p.user_id}`}
              className="bg-[#0A1F18]/80 border border-sheriff-gold/30 rounded-xl p-4 flex flex-col"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-heading font-bold text-[#F5E6B8] truncate">{p.rp_name || p.email}</div>
                <span className="font-mono text-sm font-bold text-emerald-300 shrink-0">{p.best_score_pct}%</span>
              </div>
              <div className="text-xs text-[#B8A269] font-mono truncate mb-2">{p.email}</div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.12em] font-bold rounded-full bg-[#143830] border border-sheriff-gold/30 text-sheriff-gold px-2 py-0.5">
                  {p.current_role || "—"}
                </span>
                <span className="text-sheriff-gold/60">→</span>
                <span className="text-[10px] uppercase tracking-[0.12em] font-bold rounded-full bg-sheriff-gold/15 border border-sheriff-gold/60 text-sheriff-gold px-2 py-0.5">
                  {targetRole}
                </span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-sheriff-gold/70 mb-3">
                {p.attempts_count} {p.attempts_count === 1 ? "tentativo" : "tentativi"} · {p.exam_tier_label || "Vice Sceriffo"}
              </div>
              <Button
                onClick={() => handlePromote(p)}
                disabled={isPromoting}
                data-testid={`promote-btn-${p.user_id}`}
                className="mt-auto bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase tracking-[0.1em] text-xs"
              >
                {isPromoting
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                  : <Award className="h-3.5 w-3.5 mr-2" />}
                Promuovi a {targetRole}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="uppercase-label text-[#B8A269] mb-0.5">{label}</div>
      <div className={`text-[#F5E6B8] ${mono ? "font-mono-codes" : ""}`}>{value}</div>
    </div>
  );
}

function OpenGradingButtons({ attemptId, questionId, currentGrade, onGraded }) {
  const [loading, setLoading] = useState(null); // 'correct' | 'wrong' | 'clear' | null

  const send = async (action, manual_grade) => {
    setLoading(action);
    try {
      const { data } = await api.patch(`/quiz/admin/attempts/${attemptId}/grade-open`, {
        question_id: questionId,
        manual_grade,
      });
      // Risposta dal server: { id, correct_count, total, score_pct, passed, details }
      const updatedDetail = {
        id: data.id,
        correct_count: data.correct_count,
        total: data.total,
        score_pct: data.score_pct,
        passed: data.passed,
        details: data.details,
        // preserve fields not returned
        user_rp_name: undefined,
        submitted_at: undefined,
      };
      // Per evitare di perdere user_rp_name e submitted_at, recupero il dettaglio completo
      const fresh = await api.get(`/quiz/admin/attempts/${attemptId}`);
      onGraded?.(fresh.data);
      const msg = action === "correct" ? "Risposta valutata: corretta"
        : action === "wrong" ? "Risposta valutata: sbagliata"
        : "Valutazione annullata";
      toast.success(msg);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore valutazione");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap gap-2 items-center">
      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/70 mr-1">Valutazione HR:</span>
      <Button
        size="sm"
        onClick={() => send("correct", true)}
        disabled={loading !== null}
        data-testid={`grade-correct-${questionId}`}
        className={`rounded-full uppercase-label text-xs ${
          currentGrade === true
            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
            : "bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40"
        }`}
      >
        {loading === "correct" ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Check className="h-3 w-3 mr-1.5" />}
        Corretta
      </Button>
      <Button
        size="sm"
        onClick={() => send("wrong", false)}
        disabled={loading !== null}
        data-testid={`grade-wrong-${questionId}`}
        className={`rounded-full uppercase-label text-xs ${
          currentGrade === false
            ? "bg-rose-600 hover:bg-rose-700 text-white"
            : "bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/40"
        }`}
      >
        {loading === "wrong" ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <X className="h-3 w-3 mr-1.5" />}
        Sbagliata
      </Button>
      {currentGrade !== null && currentGrade !== undefined && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => send("clear", null)}
          disabled={loading !== null}
          data-testid={`grade-clear-${questionId}`}
          className="rounded-full uppercase-label text-xs border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10"
        >
          Annulla
        </Button>
      )}
    </div>
  );
}


// ============================================================
// AGENT EXAMS PANEL — bandi e classifiche per sergente/tenente/capitano
// ============================================================
const AGENT_TIERS = [
  { key: "sergente", label: "Sergente" },
  { key: "tenente", label: "Tenente" },
  { key: "capitano", label: "Capitano" },
];

function AgentExamsPanel() {
  const [selectedTier, setSelectedTier] = useState("sergente");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [promotingId, setPromotingId] = useState(null);
  const [viewAttempt, setViewAttempt] = useState(null);

  const reload = async (tier = selectedTier) => {
    setLoading(true);
    try {
      const [rankRes, sessRes] = await Promise.all([
        api.get(`/quiz/admin/sessions/${tier}/ranking`),
        api.get("/quiz/admin/sessions"),
      ]);
      setData(rankRes.data);
      setSessions(sessRes.data);
    } catch {
      toast.error("Errore caricamento classifica");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rankRes, sessRes] = await Promise.all([
          api.get(`/quiz/admin/sessions/${selectedTier}/ranking`),
          api.get("/quiz/admin/sessions"),
        ]);
        if (cancelled) return;
        setData(rankRes.data);
        setSessions(sessRes.data);
      } catch {
        if (!cancelled) toast.error("Errore caricamento classifica");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTier]);

  const handlePromote = async (entry) => {
    const target = data?.next_role || "—";
    if (!window.confirm(`Confermi la promozione di ${entry.rp_name || entry.email} a ${target}?`)) return;
    setPromotingId(entry.user_id);
    try {
      const res = await api.post(`/quiz/admin/promote/${entry.user_id}`);
      toast.success(res.data?.message || `${entry.rp_name || entry.email} promosso a ${target}`);
      reload();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore promozione";
      toast.error(typeof detail === "string" ? detail : "Errore promozione");
    } finally {
      setPromotingId(null);
    }
  };

  const handleView = async (entry) => {
    try {
      const { data: att } = await api.get(`/quiz/admin/attempts/${entry.attempt_id}`);
      setViewAttempt(att);
    } catch {
      toast.error("Errore caricamento esame");
    }
  };

  return (
    <div className="space-y-5">
      {/* Tier selector */}
      <div className="grid grid-cols-3 gap-2">
        {AGENT_TIERS.map((t) => {
          const sess = sessions.find((s) => s.tier === t.key);
          const isOpen = sess?.open;
          const promoted = sess?.promoted_count || 0;
          const slots = sess?.slots || 0;
          return (
            <button
              key={t.key}
              data-testid={`hr-tier-tab-${t.key}`}
              onClick={() => setSelectedTier(t.key)}
              className={`text-left rounded-xl border p-3 transition-all ${
                selectedTier === t.key
                  ? "border-sheriff-gold bg-sheriff-gold/15 text-sheriff-gold"
                  : "border-sheriff-gold/20 bg-[#0F2620] text-[#E8D89A] hover:border-sheriff-gold/60"
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <div className="font-bold text-sm">{t.label}</div>
                {isOpen ? (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.15em] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">Aperto</span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-[0.15em] font-bold bg-rose-500/10 text-rose-300 border border-rose-500/30">Chiuso</span>
                )}
              </div>
              <div className="text-[10px] text-[#8B7B49] mt-0.5">
                {isOpen
                  ? `${promoted}/${slots} promossi`
                  : sess?.auto_closed
                    ? "Auto-chiuso · posti esauriti"
                    : "Nessun bando attivo"}
              </div>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-sheriff-gold" /></div>
      ) : !data ? null : (
        <RankingTable data={data} promotingId={promotingId} onPromote={handlePromote} onView={handleView} onReload={reload} />
      )}

      <AttemptViewDialog attempt={viewAttempt} onClose={() => setViewAttempt(null)} />
    </div>
  );
}

function AttemptViewDialog({ attempt, onClose }) {
  return (
    <Dialog open={!!attempt} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-gradient-to-br from-[#143830] to-[#0A1F18] border-sheriff-gold/40 text-[#F5E6B8] rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="agent-attempt-view-dialog">
        {attempt && (
          <>
            <DialogHeader>
              <div className="uppercase-label text-sheriff-gold mb-1">Esame Agente · Sola lettura</div>
              <DialogTitle className="font-heading font-black text-2xl text-[#F5E6B8]">
                {attempt.user_rp_name}
                <span className={`ml-3 font-mono ${attempt.passed ? "text-emerald-300" : "text-rose-300"}`}>
                  {attempt.correct_count}/{attempt.total} · {attempt.score_pct}%
                </span>
              </DialogTitle>
              <div className="text-xs text-[#B8A269] font-mono mt-1">{new Date(attempt.submitted_at).toLocaleString("it-IT")}</div>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              {attempt.details.map((d, i) => {
                const borderCls = d.is_correct ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5";
                const icon = d.is_correct
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
                  : <X className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />;
                return (
                  <div key={d.question_id} className={`p-4 rounded-xl border ${borderCls}`}>
                    <div className="flex items-start gap-2">
                      {icon}
                      <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-sheriff-gold/60 mb-1">
                          Domanda {i + 1}{d.category ? ` · ${d.category}` : ""}
                        </div>
                        <div className="font-semibold text-[#F5E6B8] mb-2">{d.text}</div>
                        <div className="text-xs text-[#B8A269]">
                          Risposta data:{" "}
                          <span className="font-mono text-[#E8D89A]">
                            {d.selected_index != null && d.options ? `${String.fromCharCode(65 + d.selected_index)}) ${d.options[d.selected_index]}` : "— nessuna —"}
                          </span>
                        </div>
                        <div className="text-xs text-emerald-300 mt-0.5">
                          Risposta corretta:{" "}
                          <span className="font-mono">
                            {d.options ? `${String.fromCharCode(65 + d.correct_index)}) ${d.options[d.correct_index]}` : "—"}
                          </span>
                        </div>
                        {d.explanation && (
                          <div className="mt-2 text-[10px] text-sheriff-gold/60 italic">Nota: {d.explanation}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RankingTable({ data, promotingId, onPromote, onView, onReload }) {
  const { ranking, session, next_role, tier_label } = data;
  const tier = session?.tier;
  const slots = session?.slots || 0;
  const promoted = session?.promoted_count || 0;
  const remaining = Math.max(slots - promoted, 0);
  const isOpen = session?.open;
  const eligible = ranking.filter((r) => r.still_eligible);
  const topNIds = new Set(eligible.slice(0, remaining || slots).map((r) => r.user_id));

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border-2 p-5 ${isOpen ? "border-sheriff-gold/60 bg-gradient-to-br from-sheriff-gold/15 to-[#0F2620]" : "border-sheriff-gold/25 bg-[#143830]"}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="uppercase-label text-sheriff-gold mb-1 flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5" /> Classifica</div>
            <h3 className="font-heading font-bold text-xl text-[#F5E6B8]">
              Promozione a {next_role}
            </h3>
            <p className="text-sm text-[#B8A269] mt-1">
              {isOpen
                ? `Bando aperto · ${promoted}/${slots} promossi · ${remaining} ${remaining === 1 ? "posto rimanente" : "posti rimanenti"}`
                : session?.auto_closed
                  ? "Bando chiuso automaticamente (posti esauriti)."
                  : "Nessun bando attivo. Visualizzi la classifica dell'ultimo bando."}
            </p>
          </div>
          <div className="flex items-start gap-3 flex-wrap">
            {tier && (
              <ResetAgentCooldownButton tier={tier} tierLabel={tier_label || next_role} onDone={onReload} />
            )}
            <div className="text-right">
              <div className="font-mono text-2xl font-black text-sheriff-gold">{eligible.length}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#8B7B49]">candidati idonei</div>
            </div>
          </div>
        </div>
      </div>

      {ranking.length === 0 ? (
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-10 text-center">
          <Trophy className="h-10 w-10 text-sheriff-gold/40 mx-auto mb-3" />
          <p className="text-[#B8A269]">Nessun candidato ha ancora sostenuto l&apos;esame {tier_label}.</p>
        </div>
      ) : (
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-sheriff-gold/15">
                <TableHead className="text-sheriff-gold text-center w-12">#</TableHead>
                <TableHead className="text-sheriff-gold">Candidato</TableHead>
                <TableHead className="text-sheriff-gold text-center">Punteggio</TableHead>
                <TableHead className="text-sheriff-gold text-center">Esito</TableHead>
                <TableHead className="text-sheriff-gold text-right">Azione</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ranking.map((r, i) => {
                const isTop = topNIds.has(r.user_id);
                const isPromoting = promotingId === r.user_id;
                return (
                  <TableRow
                    key={r.attempt_id}
                    data-testid={`ranking-row-${i}`}
                    className={`border-sheriff-gold/10 ${isTop ? "bg-sheriff-gold/5" : ""}`}
                  >
                    <TableCell className="text-center">
                      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-full font-mono font-bold text-xs ${
                        isTop ? "bg-sheriff-gold/25 text-sheriff-gold border border-sheriff-gold/60" : "bg-[#0F2620] text-[#8B7B49] border border-sheriff-gold/15"
                      }`}>
                        {i + 1}
                      </span>
                    </TableCell>
                    <TableCell className="text-[#F5E6B8] font-semibold">
                      <div>{r.rp_name || "—"}</div>
                      <div className="text-[10px] text-[#8B7B49] font-mono mt-0.5">{r.email}</div>
                      {!r.still_eligible && (
                        <div className="text-[9px] uppercase tracking-[0.1em] text-rose-300/70 mt-1">Non più idoneo · ruolo cambiato</div>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-mono font-bold text-sheriff-gold whitespace-nowrap">
                      {r.correct_count}/{r.total} · {r.score_pct}%
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-bold border ${r.passed ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border-rose-500/40"}`}>
                        {r.passed ? "Superato" : "Bocciato"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onView(r)}
                          data-testid={`ranking-view-${i}`}
                          className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase tracking-[0.1em] text-[10px]"
                        >
                          <Eye className="h-3 w-3 mr-1.5" /> Visualizza
                        </Button>
                        {r.still_eligible && r.passed ? (
                          <Button
                            size="sm"
                            onClick={() => onPromote(r)}
                            disabled={isPromoting}
                            data-testid={`ranking-promote-${i}`}
                            className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold rounded-full uppercase tracking-[0.1em] text-[10px]"
                          >
                            {isPromoting
                              ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                              : <Award className="h-3 w-3 mr-1.5" />}
                            Promuovi
                          </Button>
                        ) : (
                          <span className="text-[10px] text-[#8B7B49] uppercase tracking-[0.1em]">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}


// ============================================================
// ServiceHoursPanel — pannello Ore di Servizio (HR)
// ============================================================
const DAY_LABELS = ["L", "M", "M", "G", "V", "S", "D"];

function fmtHM(seconds) {
  if (!seconds || seconds < 60) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function Sparkline({ daily, max }) {
  // 7 mini barre compatte lun→dom. Se tutto 0 → mostra placeholder discreto.
  const hasData = daily.some((s) => s > 0);
  if (!hasData) {
    return (
      <div className="flex items-center gap-1 w-28 h-5 opacity-40" title="Nessuna attività questa settimana">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={`empty-${DAY_LABELS[i] || i}`} className="flex-1 h-px bg-sheriff-gold/30 rounded-full" />
        ))}
      </div>
    );
  }
  const safeMax = Math.max(max, 1);
  return (
    <div className="flex items-end gap-1 w-28 h-5" title={`Lun→Dom: ${daily.map((s, i) => `${DAY_LABELS[i]}=${fmtHM(s)}`).join(" · ")}`}>
      {daily.map((s, i) => {
        const pct = (s / safeMax) * 100;
        const h = s === 0 ? 4 : Math.max(pct, 18);
        return (
          <div
            key={`bar-${DAY_LABELS[i] || i}`}
            className={`flex-1 rounded-sm ${s > 0 ? "bg-sheriff-gold/80" : "bg-sheriff-gold/15"}`}
            style={{ height: `${h}%`, minWidth: 3 }}
          />
        );
      })}
    </div>
  );
}

function ServiceHoursPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ operators: [], week_start: null, week_end: null });
  const [active, setActive] = useState({ active: [], count: 0 });
  const [filter, setFilter] = useState("");
  const [historyOpen, setHistoryOpen] = useState(null); // { user_id, rp_name }
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [warnDialog, setWarnDialog] = useState(null); // { user_id, rp_name, role }
  const [warnReason, setWarnReason] = useState("");
  const [warnActionType, setWarnActionType] = useState("Richiamo 1");
  const [warning, setWarning] = useState(false);
  // FERIE
  const [vacOpen, setVacOpen] = useState(null); // { user_id, rp_name }
  const [vacHistory, setVacHistory] = useState([]);
  const [vacLoading, setVacLoading] = useState(false);
  // DIMISSIONI
  const [resignOpen, setResignOpen] = useState(null); // resignation object
  const [resignDeciding, setResignDeciding] = useState(false);
  const [resignRejectNote, setResignRejectNote] = useState("");
  // CONVOCAZIONE INATTIVI (mass-select + Discord embed unico)
  const [inactiveSelection, setInactiveSelection] = useState(() => new Set());
  const [summonOpen, setSummonOpen] = useState(false);
  const [summonNote, setSummonNote] = useState("");
  const [summonSending, setSummonSending] = useState(false);

  const toggleInactive = useCallback((userId) => {
    setInactiveSelection((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }, []);
  const clearInactiveSelection = useCallback(() => setInactiveSelection(new Set()), []);

  const submitInactivitySummon = async () => {
    if (inactiveSelection.size === 0) return;
    setSummonSending(true);
    try {
      const { data } = await api.post("/hr/inactivity-summon", {
        user_ids: Array.from(inactiveSelection),
        note: summonNote.trim(),
      });
      const disc = data?.discord;
      if (disc?.ok) {
        toast.success(`Convocazione pubblicata su Discord (${data.summoned_count} operatori taggati)`);
      } else {
        toast.error(`Discord: ${disc?.reason || "invio fallito"}${data.missing_discord?.length ? ` · ${data.missing_discord.length} senza Discord ID` : ""}`);
      }
      setSummonOpen(false);
      setSummonNote("");
      clearInactiveSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convocazione fallita");
    } finally {
      setSummonSending(false);
    }
  };

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [w, a] = await Promise.all([
        api.get("/shifts/weekly"),
        api.get("/shifts/active"),
      ]);
      setData(w.data || { operators: [] });
      setActive(a.data || { active: [], count: 0 });
    } catch {
      toast.error("Errore nel caricamento dei dati di servizio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, 30000); // refresh ogni 30s
    return () => clearInterval(id);
  }, [reload]);

  const operators = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (data.operators || []).filter((o) =>
      !q || (o.rp_name || "").toLowerCase().includes(q) || (o.role || "").toLowerCase().includes(q)
    );
  }, [data.operators, filter]);

  const maxDaily = useMemo(() => {
    let m = 0;
    for (const op of operators) {
      for (const s of (op.daily_seconds || [])) if (s > m) m = s;
    }
    return m;
  }, [operators]);

  const openHistory = async (op) => {
    setHistoryOpen({ user_id: op.user_id, rp_name: op.rp_name });
    setHistoryLoading(true);
    setHistory([]);
    try {
      const { data } = await api.get(`/shifts/user/${op.user_id}/history`, { params: { limit: 30 } });
      setHistory(data.history || []);
    } catch {
      toast.error("Storico non disponibile");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openVacationHistory = async (op) => {
    setVacOpen({ user_id: op.user_id, rp_name: op.rp_name });
    setVacLoading(true);
    setVacHistory([]);
    try {
      const { data } = await api.get(`/users/${op.user_id}/vacations`);
      setVacHistory(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Storico ferie non disponibile");
    } finally {
      setVacLoading(false);
    }
  };

  const decideVacation = async (vacId, approve, note) => {
    try {
      await api.post(`/vacations/${vacId}/${approve ? "approve" : "reject"}`, { note: note || null });
      toast.success(`Ferie ${approve ? "approvate" : "rifiutate"}`);
      if (vacOpen) openVacationHistory({ user_id: vacOpen.user_id, rp_name: vacOpen.rp_name });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento ferie");
    }
  };

  const openResignation = async (resignationId) => {
    try {
      const { data } = await api.get(`/resignations`, { params: { status: "pending" } });
      const found = (data || []).find((r) => r.id === resignationId);
      if (!found) {
        toast.error("Richiesta non più disponibile");
        reload();
        return;
      }
      setResignOpen(found);
      setResignRejectNote("");
    } catch (e) {
      toast.error("Errore caricamento richiesta");
    }
  };

  const decideResignation = async (approve) => {
    if (!resignOpen) return;
    if (!approve && resignRejectNote.trim().length < 3) {
      toast.error("Aggiungi una motivazione per il rifiuto");
      return;
    }
    setResignDeciding(true);
    try {
      await api.post(`/resignations/${resignOpen.id}/${approve ? "approve" : "reject"}`, {
        note: approve ? (resignRejectNote.trim() || null) : resignRejectNote.trim(),
      });
      toast.success(`Dimissioni ${approve ? "approvate" : "rifiutate"}. DM inviato all'operatore.`);
      setResignOpen(null);
      setResignRejectNote("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore decisione");
    } finally {
      setResignDeciding(false);
    }
  };

  const forceClose = async (shiftId, rpName) => {
    if (!confirm(`Forzare la chiusura del turno di ${rpName}?`)) return;
    try {
      await api.post(`/shifts/${shiftId}/close`);
      toast.success("Turno chiuso");
      reload();
      if (historyOpen) openHistory({ user_id: historyOpen.user_id, rp_name: historyOpen.rp_name });
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "chiusura fallita"}`);
    }
  };

  const deleteShift = async (shiftId, rpName) => {
    if (!confirm(`Eliminare definitivamente questa sessione di ${rpName}? L'azione non è reversibile.`)) return;
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success("Sessione eliminata");
      reload();
      if (historyOpen) openHistory({ user_id: historyOpen.user_id, rp_name: historyOpen.rp_name });
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "delete fallito"}`);
    }
  };

  const confirmWarn = async () => {
    if (!warnDialog) return;
    if (warnReason.trim().length < 10) {
      toast.error("Motivazione richiesta (min 10 caratteri)");
      return;
    }
    setWarning(true);
    try {
      const { data } = await api.post(`/users/${warnDialog.user_id}/warn`, {
        reason: warnReason.trim(),
        action_type: warnActionType,
      });
      if (data?.ok) {
        const msg = [
          `${warnDialog.rp_name}: ${warnActionType} emesso`,
          data.channel_posted ? "annuncio Discord pubblicato" : "⚠️ webhook Discord non configurato",
          data.roles_added?.length ? `ruoli: ${data.roles_added.join(" + ")}` : null,
          data.roles_missing?.length ? `mancanti: ${data.roles_missing.join(", ")}` : null,
        ].filter(Boolean).join(" · ");
        toast.success(msg, { duration: 10000 });
        setWarnDialog(null);
        setWarnReason("");
        setWarnActionType("Richiamo 1");
      } else {
        toast.error(`Errore: ${data?.reason || "provvedimento fallito"}`);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error("Non autorizzato — solo HR/Comando può emettere provvedimenti.");
      } else {
        toast.error(`Errore: ${e?.response?.data?.detail || "provvedimento fallito"}`);
      }
    } finally {
      setWarning(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-[#D4C480]"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento ore di servizio…</div>;
  }

  return (
    <div className="space-y-6" data-testid="service-hours-panel">
      {/* Riepilogo top */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-5">
          <div className="flex items-center gap-2 text-emerald-300 mb-2">
            <Activity className="h-4 w-4" />
            <span className="uppercase-label text-[10px]">In Servizio Adesso</span>
          </div>
          <div className="font-heading font-black text-4xl text-emerald-100 tracking-tighter">{active.count}</div>
          <div className="text-xs text-emerald-200/80 mt-1">
            {active.active.slice(0, 3).map((s) => s.rp_name).join(", ") || "Nessun operatore in servizio"}
            {active.count > 3 && ` +${active.count - 3}`}
          </div>
        </div>
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5">
          <div className="flex items-center gap-2 text-amber-300 mb-2">
            <Palmtree className="h-4 w-4" />
            <span className="uppercase-label text-[10px]">Operatori in Ferie</span>
          </div>
          <div className="font-heading font-black text-4xl text-amber-100 tracking-tighter">
            {operators.filter((o) => o.on_vacation).length}
          </div>
          <div className="text-xs text-amber-200/80 mt-1 truncate">
            {operators.filter((o) => o.on_vacation).slice(0, 3).map((o) => o.rp_name).join(", ") || "Nessun operatore in ferie"}
          </div>
        </div>
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-5">
          <div className="flex items-center gap-2 text-red-300 mb-2">
            <XCircle className="h-4 w-4" />
            <span className="uppercase-label text-[10px]">Sanzioni & Dimissioni Attive</span>
          </div>
          <div className="font-heading font-black text-4xl text-red-100 tracking-tighter">
            {operators.reduce((n, o) => n + ((o.active_sanctions || []).length) + (o.pending_resignation_id ? 1 : 0), 0)}
          </div>
          <div className="text-xs text-red-200/80 mt-1">
            {(() => {
              const sanc = operators.reduce((n, o) => n + (o.active_sanctions || []).length, 0);
              const dim = operators.filter((o) => o.pending_resignation_id).length;
              return `${sanc} sanzion${sanc === 1 ? "e" : "i"} · ${dim} dimission${dim === 1 ? "e" : "i"} pending`;
            })()}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <input
          type="search"
          placeholder="Filtra per nome RP o grado…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 bg-[#0F2620] border border-sheriff-gold/25 rounded-full px-4 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold/60"
          data-testid="service-hours-filter"
        />
        <Button onClick={reload} variant="outline" size="sm" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full" data-testid="service-hours-refresh">
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      {/* Barra convocazione inattivi (compare quando c'è almeno un operatore selezionato) */}
      {inactiveSelection.size > 0 && (
        <div
          className="flex items-center gap-3 rounded-2xl border border-orange-400/50 bg-orange-500/10 px-5 py-3.5"
          data-testid="inactive-summon-bar"
        >
          <BellRing className="h-5 w-5 text-orange-300 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-orange-100 text-sm">
              {inactiveSelection.size} operator{inactiveSelection.size === 1 ? "e" : "i"} selezionat{inactiveSelection.size === 1 ? "o" : "i"} per convocazione
            </div>
            <div className="text-xs text-orange-200/80">
              Il bot Discord pubblicherà un unico messaggio nel canale dedicato taggando tutti i selezionati.
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={clearInactiveSelection}
            data-testid="inactive-clear-btn"
            className="border-orange-400/40 text-orange-200 hover:bg-orange-500/15"
          >
            Annulla
          </Button>
          <Button
            onClick={() => setSummonOpen(true)}
            data-testid="inactive-summon-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
          >
            <Send className="h-4 w-4 mr-2" /> Convoca su Discord
          </Button>
        </div>
      )}

      {/* Tabella operatori */}
      <div className="bg-[#143830] border border-sheriff-gold/25 rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-sheriff-gold/20">
              <TableHead className="text-sheriff-gold w-10" data-testid="inactive-select-header">
                <span className="sr-only">Seleziona per convocazione</span>
              </TableHead>
              <TableHead className="text-sheriff-gold">Operatore</TableHead>
              <TableHead className="text-sheriff-gold">Grado</TableHead>
              <TableHead className="text-sheriff-gold text-right">Settimana</TableHead>
              <TableHead className="text-sheriff-gold">Andamento lun→dom</TableHead>
              <TableHead className="text-sheriff-gold text-center">Sessioni</TableHead>
              <TableHead className="text-sheriff-gold">Stato</TableHead>
              <TableHead className="text-sheriff-gold text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.map((op) => (
              <TableRow key={op.user_id} className="border-sheriff-gold/10 hover:bg-sheriff-gold/5" data-testid={`shift-row-${op.user_id}`}>
                <TableCell className="w-10">
                  <input
                    type="checkbox"
                    checked={inactiveSelection.has(op.user_id)}
                    onChange={() => toggleInactive(op.user_id)}
                    aria-label={`Seleziona ${op.rp_name} per convocazione inattività`}
                    title="Seleziona per convocazione inattività"
                    data-testid={`inactive-checkbox-${op.user_id}`}
                    className="h-4 w-4 rounded border-sheriff-gold/40 bg-[#0F2620] text-orange-500 accent-orange-500 focus:ring-1 focus:ring-orange-400/60 cursor-pointer"
                  />
                </TableCell>
                <TableCell className="text-[#F5E6B8] font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span>{op.rp_name}</span>
                    {op.on_vacation && (
                      <Badge
                        className="bg-amber-400/20 text-amber-300 border-amber-400/60 uppercase-label text-[10px] font-bold tracking-wider"
                        data-testid={`vacation-badge-${op.user_id}`}
                        title={op.vacation_end_date ? `Rientro: ${new Date(op.vacation_end_date).toLocaleDateString("it-IT")}` : "In ferie"}
                      >
                        <Palmtree className="h-3 w-3 mr-1" />FERIE
                      </Badge>
                    )}
                    {op.pending_resignation_id && (
                      <button
                        onClick={() => openResignation(op.pending_resignation_id)}
                        title="Richiesta dimissioni pending — clicca per visionare"
                        data-testid={`resignation-badge-${op.user_id}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-400/60 uppercase text-[10px] font-bold tracking-wider hover:bg-rose-500/30 transition-colors"
                      >
                        <LogOut className="h-3 w-3" />DIMISSIONI
                      </button>
                    )}
                    {(op.active_sanctions || []).map((s) => {
                      const isSuspension = s.action_type.startsWith("Sospensione");
                      const isPerm = s.action_type === "Sospensione tempo indeterminato";
                      const endLabel = s.end_at ? new Date(s.end_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : (isPerm ? "∞" : null);
                      const tooltip = `${s.action_type}${endLabel && !isPerm ? ` — scade il ${endLabel}` : ""}${isPerm ? " — permanente" : ""}\n"${s.reason || ""}"`;
                      return (
                        <span
                          key={s.id}
                          title={tooltip}
                          data-testid={`sanction-badge-${op.user_id}-${s.id}`}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full uppercase text-[10px] font-bold tracking-wider border ${
                            isSuspension
                              ? "bg-red-600/25 text-red-200 border-red-500/70"
                              : "bg-orange-500/20 text-orange-200 border-orange-400/60"
                          }`}
                        >
                          <XCircle className="h-3 w-3" />
                          {isSuspension ? (isPerm ? "SOSPESO ∞" : `SOSPESO al ${endLabel}`) : s.action_type.toUpperCase()}
                        </span>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell className="text-[#D4C480] text-sm">{op.role || "—"}</TableCell>
                <TableCell className="text-right font-mono text-sheriff-gold">{fmtHM(op.total_seconds)}</TableCell>
                <TableCell><Sparkline daily={op.daily_seconds || [0,0,0,0,0,0,0]} max={maxDaily} /></TableCell>
                <TableCell className="text-center text-[#D4C480]">{op.sessions_count}</TableCell>
                <TableCell>
                  {op.active ? (
                    <Badge className="bg-emerald-400/15 text-emerald-300 border-emerald-400/40 uppercase-label text-[10px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5" />In Servizio
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-[#8B7B49] uppercase tracking-[0.1em]">Off-duty</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button onClick={() => openHistory(op)} variant="ghost" size="sm" className="text-sheriff-gold hover:bg-sheriff-gold/10 text-xs" data-testid={`shift-history-${op.user_id}`}>
                      <History className="h-3.5 w-3.5 mr-1" /> Storico
                    </Button>
                    <Button onClick={() => openVacationHistory(op)} variant="ghost" size="sm" className="text-amber-300 hover:bg-amber-400/10 text-xs" data-testid={`vacation-history-${op.user_id}`}>
                      <Palmtree className="h-3.5 w-3.5 mr-1" /> Ferie
                    </Button>
                    <Button onClick={() => { setWarnDialog({ user_id: op.user_id, rp_name: op.rp_name, role: op.role }); setWarnReason(""); setWarnActionType("Richiamo 1"); }} variant="ghost" size="sm" className="text-amber-300 hover:bg-amber-400/15 hover:text-amber-200 text-xs" data-testid={`warn-${op.user_id}`}>
                      <Award className="h-3.5 w-3.5 mr-1 rotate-180" /> Provvedimento
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {operators.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-[#8B7B49] py-8">Nessun operatore trovato</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog storico */}
      <Dialog open={!!historyOpen} onOpenChange={(o) => !o && setHistoryOpen(null)}>
        <DialogContent className="bg-[#0F2620] border-sheriff-gold/40 max-w-2xl" data-testid="shift-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F5E6B8] font-heading">Storico — {historyOpen?.rp_name}</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center gap-2 text-[#D4C480] py-6"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
          ) : history.length === 0 ? (
            <div className="text-center text-[#8B7B49] py-8">Nessuna sessione registrata</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {history.map((h) => {
                const start = h.started_at ? new Date(h.started_at) : null;
                const end = h.ended_at ? new Date(h.ended_at) : null;
                const durSec = (h.duration_minutes || 0) * 60;
                return (
                  <div key={h.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#143830] border border-sheriff-gold/15">
                    <div className={`w-2 h-2 rounded-full ${h.status === "open" ? "bg-emerald-400 animate-pulse" : h.status === "auto_closed" ? "bg-amber-400" : "bg-sheriff-gold/60"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#F5E6B8]">
                        {start ? start.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                        {end && ` → ${end.toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit" })}`}
                        {!end && <span className="ml-2 text-emerald-300 text-xs uppercase">in corso</span>}
                      </div>
                      <div className="text-xs text-[#8B7B49] mt-0.5">
                        Durata: {fmtHM(durSec)} · {h.status === "auto_closed" ? "Chiuso automaticamente" : h.ended_by?.startsWith("hr_force") ? "Chiusura forzata HR" : "Sessione standard"}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {h.status === "open" && (
                        <Button onClick={() => forceClose(h.id, historyOpen.rp_name)} variant="ghost" size="sm" className="text-amber-300 hover:bg-amber-400/10 text-xs" data-testid={`force-close-${h.id}`}>
                          Chiudi
                        </Button>
                      )}
                      <Button onClick={() => deleteShift(h.id, historyOpen.rp_name)} variant="ghost" size="sm" className="text-rose-300 hover:bg-rose-400/10" data-testid={`delete-shift-${h.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog STORICO FERIE per operatore */}
      <Dialog open={!!vacOpen} onOpenChange={(o) => !o && setVacOpen(null)}>
        <DialogContent className="bg-[#0F2620] border-amber-400/40 max-w-2xl" data-testid="vacation-history-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-200 font-heading flex items-center gap-2">
              <Palmtree className="h-5 w-5" /> Storico Ferie — {vacOpen?.rp_name}
            </DialogTitle>
          </DialogHeader>
          {vacLoading ? (
            <div className="flex items-center gap-2 text-[#D4C480] py-6"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
          ) : vacHistory.length === 0 ? (
            <div className="text-center text-[#8B7B49] py-8">Nessuna richiesta ferie per questo operatore</div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {vacHistory.map((v) => {
                const statusMeta = {
                  pending: { label: "In Attesa", color: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
                  approved: { label: "Approvata", color: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40" },
                  rejected: { label: "Rifiutata", color: "bg-rose-400/15 text-rose-300 border-rose-400/40" },
                  completed: { label: "Terminata", color: "bg-sheriff-gold/15 text-sheriff-gold border-sheriff-gold/40" },
                }[v.status] || { label: v.status, color: "bg-zinc-400/15 text-zinc-300 border-zinc-400/40" };
                const fmtD = (d) => d ? new Date(d).toLocaleDateString("it-IT") : "—";
                const tipoLabel = [v.type_ic && "IC", v.type_ooc && "OOC"].filter(Boolean).join(" + ") || "—";
                return (
                  <div key={v.id} className="p-3 rounded-lg bg-[#143830] border border-amber-400/15" data-testid={`vacation-row-${v.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${statusMeta.color} uppercase-label text-[10px]`}>{statusMeta.label}</Badge>
                          <span className="text-[10px] text-amber-300/80 uppercase tracking-wider">{tipoLabel}</span>
                        </div>
                        <div className="text-sm text-[#F5E6B8] mt-2">
                          📅 <span className="font-medium">{fmtD(v.start_date)}</span> → <span className="font-medium">{fmtD(v.end_date)}</span>
                        </div>
                        {v.motivation && (
                          <div className="text-xs text-[#D4C480] mt-1.5 italic">&ldquo;{v.motivation}&rdquo;</div>
                        )}
                        {v.decision_note && (
                          <div className="text-xs text-[#8B7B49] mt-1.5">
                            <span className="uppercase tracking-wider">Note HR ({v.decided_by_rp_name || "—"}):</span> {v.decision_note}
                          </div>
                        )}
                      </div>
                      {v.status === "pending" && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            onClick={() => decideVacation(v.id, true, null)}
                            size="sm"
                            className="bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-400/40 text-xs"
                            data-testid={`vacation-approve-${v.id}`}
                          >
                            <Check className="h-3.5 w-3.5 mr-1" /> Approva
                          </Button>
                          <Button
                            onClick={() => {
                              const reason = prompt("Motivazione del rifiuto:");
                              if (reason && reason.trim()) decideVacation(v.id, false, reason.trim());
                            }}
                            size="sm"
                            className="bg-rose-500/15 text-rose-300 hover:bg-rose-500/30 border border-rose-400/40 text-xs"
                            data-testid={`vacation-reject-${v.id}`}
                          >
                            <X className="h-3.5 w-3.5 mr-1" /> Rifiuta
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Dimissioni HR */}
      <Dialog open={!!resignOpen} onOpenChange={(o) => !o && !resignDeciding && setResignOpen(null)}>
        <DialogContent className="bg-[#0F2620] border-rose-500/40 max-w-lg" data-testid="resignation-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-200 font-heading flex items-center gap-2">
              <LogOut className="h-5 w-5" /> Richiesta Dimissioni — {resignOpen?.rp_name}
            </DialogTitle>
          </DialogHeader>
          {resignOpen && (
            <div className="space-y-3 py-2 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <div className="uppercase-label text-[10px] text-rose-300/80">Operatore</div>
                  <div className="text-[#F5E6B8] font-medium">{resignOpen.rp_name}</div>
                </div>
                <div>
                  <div className="uppercase-label text-[10px] text-rose-300/80">Grado</div>
                  <div className="text-[#F5E6B8]">{resignOpen.role || "—"}</div>
                </div>
                <div>
                  <div className="uppercase-label text-[10px] text-rose-300/80">Inviata il</div>
                  <div className="text-[#D4C480]">{resignOpen.requested_at ? new Date(resignOpen.requested_at).toLocaleString("it-IT") : "—"}</div>
                </div>
                <div>
                  <div className="uppercase-label text-[10px] text-rose-300/80">Ultimo servizio</div>
                  <div className="text-[#D4C480]">{resignOpen.last_service_date || "Non specificato"}</div>
                </div>
              </div>
              <div>
                <div className="uppercase-label text-[10px] text-rose-300/80 mb-1">Motivazione dell&apos;operatore</div>
                <div className="text-[#F5E6B8] bg-[#1a0e0e] border border-rose-500/20 rounded-lg p-3 whitespace-pre-wrap">
                  {resignOpen.reason || "—"}
                </div>
              </div>
              <div>
                <div className="uppercase-label text-[10px] text-rose-300/80 mb-1">
                  Nota HR{" "}
                  <span className="text-[#8B7B49] normal-case tracking-normal">
                    (facoltativa per approvazione, obbligatoria per rifiuto)
                  </span>
                </div>
                <Textarea
                  value={resignRejectNote}
                  onChange={(e) => setResignRejectNote(e.target.value)}
                  placeholder="Nota che sarà inviata all'operatore via DM…"
                  rows={3}
                  className="bg-[#1a0e0e] border-rose-500/30 text-[#F5E6B8]"
                  data-testid="resignation-note"
                />
              </div>
              <div className="text-[11px] text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-lg p-2">
                ⚠️ Alla decisione, il bot invierà automaticamente un DM Discord all&apos;operatore
                con il messaggio appropriato (approvazione → istruzioni consegna equipaggiamento; rifiuto → nota HR).
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setResignOpen(null)} disabled={resignDeciding} className="text-[#D4C480]">
              Chiudi
            </Button>
            <Button
              onClick={() => decideResignation(false)}
              disabled={resignDeciding}
              className="bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 border border-rose-400/40"
              data-testid="resignation-reject-btn"
            >
              <X className="h-4 w-4 mr-2" /> Rifiuta
            </Button>
            <Button
              onClick={() => decideResignation(true)}
              disabled={resignDeciding}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold"
              data-testid="resignation-approve-btn"
            >
              {resignDeciding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Approva Dimissioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma provvedimento disciplinare */}
      <Dialog open={!!warnDialog} onOpenChange={(o) => { if (!o) { setWarnDialog(null); setWarnReason(""); setWarnActionType("Richiamo 1"); } }}>
        <DialogContent className="bg-[#0F2620] border-amber-400/40 max-w-md" data-testid="warn-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-200 font-heading flex items-center gap-2">
              <Award className="h-5 w-5 rotate-180" /> Provvedimento Disciplinare
            </DialogTitle>
          </DialogHeader>
          {warnDialog && (
            <div className="space-y-4">
              <div className="rounded-lg bg-amber-400/10 border border-amber-400/30 p-4">
                <div className="text-[#F5E6B8] font-medium">{warnDialog.rp_name}</div>
                <div className="text-xs text-[#D4C480]">{warnDialog.role || "—"}</div>
              </div>
              <div>
                <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Tipo provvedimento</label>
                <select
                  value={warnActionType}
                  onChange={(e) => setWarnActionType(e.target.value)}
                  className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] focus:outline-none focus:border-sheriff-gold"
                  data-testid="warn-action-type-select"
                >
                  <option value="Richiamo 1">Richiamo 1</option>
                  <option value="Richiamo 2">Richiamo 2</option>
                  <option value="Sospensione 1gg">Sospensione 1 giorno</option>
                  <option value="Sospensione 2gg">Sospensione 2 giorni</option>
                  <option value="Sospensione 5gg">Sospensione 5 giorni</option>
                  <option value="Sospensione tempo indeterminato">Sospensione tempo indeterminato</option>
                </select>
              </div>
              <p className="text-xs text-[#D4C480] leading-relaxed">
                Verrà applicato il <strong className="text-amber-200">{warnActionType}</strong>:
                <br/>· Aggiunti il ruolo Discord del provvedimento + il <code className="text-amber-200 text-[10px]">ruolo separatore</code>
                <br/>· Pubblicato un embed sul canale provvedimenti con la motivazione
                <br/>· Tracciato nell&apos;audit log
              </p>
              <div>
                <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Motivazione (min 10 caratteri)</label>
                <textarea
                  value={warnReason}
                  onChange={(e) => setWarnReason(e.target.value)}
                  rows={4}
                  placeholder="Es. Mancata partecipazione a 3 turni consecutivi, comportamento non conforme al regolamento, ecc."
                  className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold"
                  data-testid="warn-reason-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={() => { setWarnDialog(null); setWarnReason(""); setWarnActionType("Richiamo 1"); }} variant="ghost" className="text-[#D4C480]">Annulla</Button>
                <Button
                  onClick={confirmWarn}
                  disabled={warning || warnReason.trim().length < 10}
                  className="bg-amber-500 hover:bg-amber-600 text-[#0A1F18] font-bold disabled:opacity-50"
                  data-testid="warn-confirm-btn"
                >
                  {warning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Award className="h-4 w-4 mr-2 rotate-180" />}
                  Emetti Provvedimento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CONVOCAZIONE INATTIVITÀ — riepilogo + nota opzionale */}
      <Dialog open={summonOpen} onOpenChange={(o) => { if (!o && !summonSending) { setSummonOpen(false); } }}>
        <DialogContent className="bg-[#0F2620] border-orange-400/50 max-w-lg" data-testid="inactive-summon-dialog">
          <DialogHeader>
            <DialogTitle className="text-orange-100 font-heading flex items-center gap-2">
              <BellRing className="h-5 w-5" /> Convocazione per inattività
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-orange-500/10 border border-orange-400/30 p-4">
              <div className="uppercase-label text-[10px] text-orange-200/70 mb-2">
                {inactiveSelection.size} operator{inactiveSelection.size === 1 ? "e selezionato" : "i selezionati"}
              </div>
              <div className="text-sm text-[#F5E6B8] max-h-40 overflow-y-auto space-y-1">
                {operators.filter((o) => inactiveSelection.has(o.user_id)).map((o) => (
                  <div key={o.user_id} className="flex items-center justify-between gap-3">
                    <span>{o.rp_name} <span className="text-[#B8A269] text-xs">· {o.role || "—"}</span></span>
                    {!o.discord_id && (
                      <span className="text-[10px] uppercase tracking-wider text-rose-300" title="Discord ID mancante — verrà escluso">no discord</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Nota del Comando (facoltativa)</label>
              <Textarea
                value={summonNote}
                onChange={(e) => setSummonNote(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Contesto o istruzioni aggiuntive (opzionale, es. periodo di riferimento)"
                data-testid="inactive-summon-note"
                className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-[#8B7B49]"
              />
              <div className="text-[10px] text-sheriff-gold/50 text-right font-mono mt-1">{summonNote.length}/500</div>
            </div>
            <div className="text-[11px] text-[#B8A269] leading-relaxed rounded-lg bg-[#0A1F18]/60 border border-sheriff-gold/15 p-3">
              Il bot pubblicherà un <strong className="text-sheriff-gold">unico messaggio</strong> nel canale Discord dedicato (ID <code>1495469234283610324</code>) taggando tutti gli operatori.
              L&apos;embed indicherà le sanzioni previste:
              <br />· <strong>Mancata presentazione entro 7 giorni</strong> → sospensione a tempo indeterminato (ruolo <code>1437597141684977715</code>)
              <br />· <strong>Mancata consegna dell&apos;equipaggiamento</strong> → licenziamento
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setSummonOpen(false)} variant="ghost" className="text-[#D4C480]" disabled={summonSending}>Annulla</Button>
              <Button
                onClick={submitInactivitySummon}
                disabled={summonSending || inactiveSelection.size === 0}
                data-testid="inactive-summon-confirm"
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold"
              >
                {summonSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Pubblica su Discord
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================
// EXAM AUTH REQUESTS — pannello HR per approvare/rifiutare
// richieste di autorizzazione esame (V.Sce / Sgt / Lt / Cpt)
// ============================================================
const TIER_LABELS_AUTH = {
  vice_sceriffo: "Vice Sceriffo",
  sergente: "Sergente",
  tenente: "Tenente",
  capitano: "Capitano",
};

const STATUS_TONE = {
  pending: { label: "In attesa", chip: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  approved: { label: "Approvata", chip: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  rejected: { label: "Rifiutata", chip: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};

function ExamAuthRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [tierFilter, setTierFilter] = useState("");
  const [decideDialog, setDecideDialog] = useState(null); // { req, action }
  const [decisionNote, setDecisionNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null); // request object
  const [deleting, setDeleting] = useState(false);
  const [addStepDialog, setAddStepDialog] = useState(null); // request object
  const [newStepName, setNewStepName] = useState("");
  const [newStepDesc, setNewStepDesc] = useState("");
  const [savingStep, setSavingStep] = useState(false);
  const [togglingStep, setTogglingStep] = useState({}); // { "reqId:idx": bool }

  const canManageSteps = (r) => ["sergente", "tenente", "capitano"].includes(r?.exam_tier);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (tierFilter) params.tier = tierFilter;
      const { data } = await api.get("/exam-auth/admin", { params });
      setItems(data || []);
    } catch {
      toast.error("Errore caricamento richieste esami");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter]);

  useEffect(() => { reload(); }, [reload]);

  const handleDecide = async () => {
    if (!decideDialog) return;
    if (decideDialog.action === "reject" && decisionNote.trim().length < 1) {
      toast.error("Nota obbligatoria per i rifiuti");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/exam-auth/admin/${decideDialog.req.id}`, {
        action: decideDialog.action,
        decision_note: decisionNote.trim() || null,
      });
      toast.success(decideDialog.action === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDecideDialog(null);
      setDecisionNote("");
      reload();
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "operazione fallita"}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await api.delete(`/exam-auth/admin/${deleteDialog.id}`);
      toast.success(`Richiesta di ${deleteDialog.user_rp_name} eliminata`);
      setDeleteDialog(null);
      reload();
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "operazione fallita"}`);
    } finally {
      setDeleting(false);
    }
  };

  const toggleStep = async (req, idx, completed) => {
    const key = `${req.id}:${idx}`;
    setTogglingStep((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await api.patch(`/exam-auth/admin/${req.id}/steps/${idx}`, { completed });
      setItems((prev) => prev.map((r) => (r.id === req.id ? data : r)));
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "operazione fallita"}`);
    } finally {
      setTogglingStep((prev) => ({ ...prev, [key]: false }));
    }
  };

  const removeStep = async (req, idx) => {
    if (!window.confirm(`Rimuovere lo step "${req.hr_steps[idx]?.name}"?`)) return;
    const nextSteps = req.hr_steps.filter((_, i) => i !== idx).map((s) => ({ name: s.name, description: s.description }));
    try {
      const { data } = await api.put(`/exam-auth/admin/${req.id}/steps`, { steps: nextSteps });
      setItems((prev) => prev.map((r) => (r.id === req.id ? data : r)));
      toast.success("Step rimosso");
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "operazione fallita"}`);
    }
  };

  const addStep = async () => {
    if (!addStepDialog) return;
    if (newStepName.trim().length < 2) {
      toast.error("Nome dello step obbligatorio");
      return;
    }
    setSavingStep(true);
    try {
      const req = addStepDialog;
      const nextSteps = [
        ...(req.hr_steps || []).map((s) => ({ name: s.name, description: s.description })),
        { name: newStepName.trim(), description: newStepDesc.trim() },
      ];
      const { data } = await api.put(`/exam-auth/admin/${req.id}/steps`, { steps: nextSteps });
      setItems((prev) => prev.map((r) => (r.id === req.id ? data : r)));
      setAddStepDialog(null);
      setNewStepName("");
      setNewStepDesc("");
      toast.success("Step aggiunto");
    } catch (e) {
      toast.error(`Errore: ${e?.response?.data?.detail || "operazione fallita"}`);
    } finally {
      setSavingStep(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="exam-auth-admin-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] focus:outline-none focus:border-sheriff-gold" data-testid="exam-auth-status-filter">
            <option value="pending">In attesa</option>
            <option value="approved">Approvate</option>
            <option value="rejected">Rifiutate</option>
            <option value="">Tutte</option>
          </select>
        </div>
        <div>
          <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] focus:outline-none focus:border-sheriff-gold" data-testid="exam-auth-tier-filter">
            <option value="">Tutti</option>
            <option value="vice_sceriffo">Vice Sceriffo</option>
            <option value="sergente">Sergente</option>
            <option value="tenente">Tenente</option>
            <option value="capitano">Capitano</option>
          </select>
        </div>
        <Button onClick={reload} variant="outline" size="sm" className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full uppercase-label mt-6" data-testid="exam-auth-refresh">
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Ricarica
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#D4C480]"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento richieste…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#143830] border border-sheriff-gold/25 rounded-2xl p-10 text-center text-[#B8A269]">
          Nessuna richiesta trovata per i filtri selezionati.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/25 rounded-xl p-4 sm:p-5" data-testid={`exam-auth-row-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[#F5E6B8] font-bold text-base">{r.user_rp_name || r.user_email}</div>
                  <div className="text-xs text-[#D4C480]">
                    {r.user_role || "—"} · richiesta per <span className="text-sheriff-gold font-semibold">{TIER_LABELS_AUTH[r.exam_tier] || r.exam_tier}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold border ${STATUS_TONE[r.status]?.chip}`}>
                    {STATUS_TONE[r.status]?.label || r.status}
                  </span>
                  {canManageSteps(r) && (
                    <Button
                      onClick={() => setDeleteDialog(r)}
                      variant="ghost"
                      size="sm"
                      className="text-rose-300 hover:bg-rose-500/15 hover:text-rose-200 rounded-full h-8 w-8 p-0"
                      title="Elimina richiesta bando"
                      data-testid={`exam-auth-delete-${r.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {r.motivazione && (
                <div className="bg-[#0A1F18]/60 border border-sheriff-gold/15 rounded-lg p-3 mb-3">
                  <div className="text-[10px] uppercase-label text-sheriff-gold/80 mb-1">Motivazione</div>
                  <div className="text-sm text-[#E8D89A] whitespace-pre-wrap">{r.motivazione}</div>
                </div>
              )}

              {r.decision_note && (
                <div className="bg-[#0A1F18]/60 border border-rose-400/20 rounded-lg p-3 mb-3">
                  <div className="text-[10px] uppercase-label text-rose-300/80 mb-1">Nota HR ({r.decided_by_name})</div>
                  <div className="text-sm text-[#E8D89A] whitespace-pre-wrap">{r.decision_note}</div>
                </div>
              )}

              {/* ITER HR — visibile solo per bandi Sergente/Tenente/Capitano approvati */}
              {canManageSteps(r) && r.status === "approved" && (
                <div className="bg-[#0A1F18]/70 border border-sheriff-gold/25 rounded-lg p-3 mb-3" data-testid={`exam-auth-steps-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4 text-sheriff-gold" />
                      <div className="text-[10px] uppercase-label text-sheriff-gold">Iter HR di ammissione</div>
                      {r.hr_steps_total > 0 && (
                        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${r.hr_steps_all_done ? "border-emerald-400/50 text-emerald-300 bg-emerald-500/10" : "border-amber-400/40 text-amber-200 bg-amber-500/10"}`}>
                          {r.hr_steps_completed}/{r.hr_steps_total} completati
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => { setAddStepDialog(r); setNewStepName(""); setNewStepDesc(""); }}
                      variant="outline"
                      size="sm"
                      className="border-sheriff-gold/40 text-sheriff-gold hover:bg-sheriff-gold/10 rounded-full text-xs h-7"
                      data-testid={`exam-auth-add-step-${r.id}`}
                    >
                      + Aggiungi step
                    </Button>
                  </div>
                  {(!r.hr_steps || r.hr_steps.length === 0) ? (
                    <div className="text-xs text-[#B8A269] italic py-2">
                      Nessuno step configurato. Aggiungi almeno uno step per gestire l&apos;iter (es. colloquio orale) — se la lista è vuota il candidato può sostenere subito l&apos;esame.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {r.hr_steps.map((s, idx) => {
                        const key = `${r.id}:${idx}`;
                        return (
                          <div
                            key={idx}
                            className={`flex items-start gap-3 rounded-lg px-3 py-2 border ${
                              s.completed
                                ? "border-emerald-400/30 bg-emerald-500/5"
                                : "border-sheriff-gold/15 bg-[#143830]/40"
                            }`}
                            data-testid={`exam-auth-step-${r.id}-${idx}`}
                          >
                            <input
                              type="checkbox"
                              checked={!!s.completed}
                              disabled={!!togglingStep[key]}
                              onChange={(e) => toggleStep(r, idx, e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-sheriff-gold/40 bg-[#0A1F18] text-emerald-500 accent-emerald-500 cursor-pointer"
                              data-testid={`exam-auth-step-check-${r.id}-${idx}`}
                              aria-label={`Segna "${s.name}" come completato`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium ${s.completed ? "text-emerald-200 line-through opacity-80" : "text-[#F5E6B8]"}`}>
                                {s.name}
                              </div>
                              {s.description && (
                                <div className="text-xs text-[#B8A269] mt-0.5 leading-relaxed">{s.description}</div>
                              )}
                              {s.completed && s.completed_by && (
                                <div className="text-[10px] text-emerald-300/70 mt-1 font-mono">
                                  ✓ {s.completed_by}{s.completed_at ? ` · ${new Date(s.completed_at).toLocaleString("it-IT")}` : ""}
                                </div>
                              )}
                            </div>
                            <Button
                              onClick={() => removeStep(r, idx)}
                              variant="ghost"
                              size="sm"
                              className="text-rose-300/70 hover:text-rose-200 hover:bg-rose-500/10 h-7 w-7 p-0 shrink-0"
                              title="Rimuovi step"
                              data-testid={`exam-auth-step-remove-${r.id}-${idx}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                      {r.hr_steps_all_done && (
                        <div className="text-xs text-emerald-300 mt-1 font-mono flex items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Iter HR completato — il candidato può sostenere l&apos;esame.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[#B8A269]">
                <div>
                  Inviata: <span className="font-mono text-[#D4C480]">{r.requested_at ? new Date(r.requested_at).toLocaleString("it-IT") : "—"}</span>
                  {r.decided_at && (
                    <>
                      {" · "}Decisa: <span className="font-mono text-[#D4C480]">{new Date(r.decided_at).toLocaleString("it-IT")}</span>
                    </>
                  )}
                </div>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button onClick={() => { setDecideDialog({ req: r, action: "reject" }); setDecisionNote(""); }} variant="ghost" size="sm" className="text-rose-300 hover:bg-rose-500/15 hover:text-rose-200 rounded-full text-xs" data-testid={`exam-auth-reject-${r.id}`}>
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Rifiuta
                    </Button>
                    <Button onClick={() => { setDecideDialog({ req: r, action: "approve" }); setDecisionNote(""); }} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A1F18] font-bold rounded-full text-xs px-4" data-testid={`exam-auth-approve-${r.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" /> Approva
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!decideDialog} onOpenChange={(o) => { if (!o) { setDecideDialog(null); setDecisionNote(""); } }}>
        <DialogContent className="bg-[#0F2620] border-sheriff-gold/40 max-w-md" data-testid="exam-auth-decide-dialog">
          <DialogHeader>
            <DialogTitle className="text-sheriff-gold font-heading flex items-center gap-2">
              {decideDialog?.action === "approve" ? (<><Check className="h-5 w-5 text-emerald-300" /> Approva richiesta</>) : (<><XCircle className="h-5 w-5 text-rose-300" /> Rifiuta richiesta</>)}
            </DialogTitle>
          </DialogHeader>
          {decideDialog && (
            <div className="space-y-4">
              <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-3">
                <div className="text-[#F5E6B8] font-medium">{decideDialog.req.user_rp_name}</div>
                <div className="text-xs text-[#D4C480]">Esame {TIER_LABELS_AUTH[decideDialog.req.exam_tier]}</div>
              </div>
              <div>
                <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">
                  Nota {decideDialog.action === "reject" ? "(obbligatoria)" : "(opzionale)"}
                </label>
                <textarea
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  rows={4}
                  placeholder={decideDialog.action === "approve" ? "Es. Ottimo curriculum, autorizzato." : "Es. Devi completare 5 turni in più, riprova al prossimo bando."}
                  className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold"
                  data-testid="exam-auth-note-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => { setDecideDialog(null); setDecisionNote(""); }} variant="ghost" className="text-[#D4C480]">Annulla</Button>
                <Button
                  onClick={handleDecide}
                  disabled={submitting || (decideDialog.action === "reject" && decisionNote.trim().length < 1)}
                  className={`font-bold disabled:opacity-50 ${decideDialog.action === "approve" ? "bg-emerald-500 hover:bg-emerald-600 text-[#0A1F18]" : "bg-rose-500 hover:bg-rose-600 text-white"}`}
                  data-testid="exam-auth-decide-confirm"
                >
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (decideDialog.action === "approve" ? <Check className="h-4 w-4 mr-2" /> : <XCircle className="h-4 w-4 mr-2" />)}
                  {decideDialog.action === "approve" ? "Approva" : "Rifiuta"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DIALOG: Elimina richiesta bando */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => { if (!o && !deleting) setDeleteDialog(null); }}>
        <DialogContent className="bg-[#0F2620] border-rose-400/50 max-w-md" data-testid="exam-auth-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-200 font-heading flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Elimina richiesta bando
            </DialogTitle>
          </DialogHeader>
          {deleteDialog && (
            <div className="space-y-4">
              <div className="bg-rose-500/10 border border-rose-400/30 rounded-lg p-3">
                <div className="text-[#F5E6B8] font-medium">{deleteDialog.user_rp_name}</div>
                <div className="text-xs text-[#D4C480]">
                  Bando <strong>{TIER_LABELS_AUTH[deleteDialog.exam_tier]}</strong> · stato <strong>{STATUS_TONE[deleteDialog.status]?.label}</strong>
                </div>
              </div>
              <p className="text-sm text-[#D4C480] leading-relaxed">
                La richiesta e tutti gli step HR associati verranno eliminati definitivamente.
                L&apos;operatore potrà ripresentare la richiesta se un bando è ancora aperto.
              </p>
              <div className="flex justify-end gap-2">
                <Button onClick={() => setDeleteDialog(null)} variant="ghost" className="text-[#D4C480]" disabled={deleting}>Annulla</Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
                  data-testid="exam-auth-delete-confirm"
                >
                  {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Elimina definitivamente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DIALOG: Aggiungi step HR */}
      <Dialog open={!!addStepDialog} onOpenChange={(o) => { if (!o && !savingStep) { setAddStepDialog(null); setNewStepName(""); setNewStepDesc(""); } }}>
        <DialogContent className="bg-[#0F2620] border-sheriff-gold/40 max-w-md" data-testid="exam-auth-add-step-dialog">
          <DialogHeader>
            <DialogTitle className="text-sheriff-gold font-heading flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5" /> Nuovo step iter HR
            </DialogTitle>
          </DialogHeader>
          {addStepDialog && (
            <div className="space-y-4">
              <div className="bg-sheriff-gold/10 border border-sheriff-gold/30 rounded-lg p-3">
                <div className="text-[#F5E6B8] font-medium">{addStepDialog.user_rp_name}</div>
                <div className="text-xs text-[#D4C480]">Bando {TIER_LABELS_AUTH[addStepDialog.exam_tier]}</div>
              </div>
              <div>
                <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Nome step</label>
                <input
                  value={newStepName}
                  onChange={(e) => setNewStepName(e.target.value)}
                  placeholder="Es. Colloquio orale con HR"
                  maxLength={100}
                  className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold"
                  data-testid="exam-auth-add-step-name"
                />
              </div>
              <div>
                <label className="block uppercase-label text-[10px] text-sheriff-gold mb-1.5">Descrizione (opzionale)</label>
                <textarea
                  value={newStepDesc}
                  onChange={(e) => setNewStepDesc(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Es. Verifica dell'attitudine al comando e conoscenza del regolamento."
                  className="w-full bg-[#0A1F18] border border-sheriff-gold/30 rounded-lg px-3 py-2 text-sm text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold"
                  data-testid="exam-auth-add-step-desc"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={() => { setAddStepDialog(null); setNewStepName(""); setNewStepDesc(""); }} variant="ghost" className="text-[#D4C480]" disabled={savingStep}>Annulla</Button>
                <Button
                  onClick={addStep}
                  disabled={savingStep || newStepName.trim().length < 2}
                  className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold disabled:opacity-50"
                  data-testid="exam-auth-add-step-confirm"
                >
                  {savingStep ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                  Aggiungi step
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// ReportCorrectionsPanel — segnalazione HR di correzioni su un rapporto RP.
// L'HR inserisce il numero rapporto + seleziona l'operatore. Il backend chiama
// il bot Discord che posta un embed nel canale HR_REPORT_CORRECTION_CHANNEL_ID
// pingando l'operatore e invitandolo ad aprire un ticket al PAB.
// ============================================================
function ReportCorrectionsPanel() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operatorId, setOperatorId] = useState("");
  const [reportNumber, setReportNumber] = useState("");
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/shifts/weekly");
        if (cancelled) return;
        const list = (data?.operators || [])
          .filter((o) => o.rp_name && o.rp_name !== "—")
          .map((o) => ({
            user_id: o.user_id,
            rp_name: o.rp_name,
            role: o.role || "—",
            discord_id: o.discord_id || null,
          }))
          .sort((a, b) => a.rp_name.localeCompare(b.rp_name, "it"));
        setOperators(list);
      } catch (err) {
        toast.error("Impossibile caricare la lista operatori");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return operators;
    return operators.filter(
      (o) =>
        o.rp_name.toLowerCase().includes(q) ||
        (o.role || "").toLowerCase().includes(q)
    );
  }, [operators, filter]);

  const selectedOperator = operators.find((o) => o.user_id === operatorId) || null;

  const submit = async () => {
    const rn = reportNumber.trim();
    if (!rn) {
      toast.error("Inserisci il numero del rapporto");
      return;
    }
    if (!operatorId) {
      toast.error("Seleziona un operatore");
      return;
    }
    if (!selectedOperator?.discord_id) {
      toast.error("L'operatore selezionato non ha un Discord ID collegato");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/hr/report-corrections", {
        operator_id: operatorId,
        report_number: rn,
        note: note.trim() || undefined,
      });
      if (data?.ok) {
        const noteFailed = note.trim() && data.detail?.note && !data.detail.note.ok;
        if (noteFailed) {
          toast.warning(
            `Notifica pubblica inviata, ma la nota HR non è stata pubblicata: ${data.detail.note?.reason || "errore"}`
          );
        } else {
          toast.success(
            `Notifica inviata a ${data.operator?.rp_name || "operatore"} su Discord.` +
              (note.trim() ? " Nota HR pubblicata nel canale interno." : "")
          );
        }
        setReportNumber("");
        setOperatorId("");
        setNote("");
      } else {
        const reason = data?.detail?.public?.reason || data?.detail?.reason || "Errore sconosciuto";
        toast.error(`Notifica non inviata: ${reason}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Errore rete";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="report-corrections-panel">
      <div className="rounded-2xl border border-sheriff-gold/25 bg-gradient-to-br from-[#143830] to-[#0F2620] p-6">
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <FileCheck2 className="h-4 w-4" /> Personnel Administration Bureau
        </div>
        <h2 className="font-heading font-black text-2xl text-[#F5E6B8] tracking-tighter mb-2">
          Segnalazione Correzione Rapporto
        </h2>
        <p className="text-[#D4C480]/90 text-sm max-w-3xl leading-relaxed">
          Inserisci il numero del rapporto RP che presenta imprecisioni e seleziona l'operatore
          che deve correggerlo. Il bot invierà una notifica automatica su Discord con il ping
          all'operatore e l'invito ad aprire un ticket al Personnel Administration Bureau.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colonna sinistra: form */}
        <div className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620] p-6 space-y-5">
          <div>
            <label className="uppercase-label text-sheriff-gold/80 text-[10px] block mb-2">
              Numero Rapporto
            </label>
            <input
              type="text"
              value={reportNumber}
              onChange={(e) => setReportNumber(e.target.value)}
              placeholder="es. R-2026-0142"
              maxLength={64}
              className="w-full h-11 rounded-lg bg-[#0A1F18] border border-sheriff-gold/30 focus:border-sheriff-gold text-[#F5E6B8] px-3 outline-none transition-colors"
              data-testid="report-number-input"
            />
          </div>

          <div>
            <label className="uppercase-label text-sheriff-gold/80 text-[10px] block mb-2">
              Operatore Coinvolto
            </label>
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Cerca per nome o grado…"
              className="w-full h-10 rounded-lg bg-[#0A1F18] border border-sheriff-gold/20 focus:border-sheriff-gold/60 text-[#F5E6B8] px-3 outline-none text-sm mb-3"
              data-testid="operator-filter-input"
            />
            <div
              className="max-h-72 overflow-y-auto rounded-lg border border-sheriff-gold/20 bg-[#0A1F18]/60"
              data-testid="operators-list"
            >
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-sheriff-gold" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-8 text-center text-sm text-[#D4C480]/60">
                  Nessun operatore trovato
                </div>
              ) : (
                filtered.map((op) => (
                  <button
                    key={op.user_id}
                    onClick={() => setOperatorId(op.user_id)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left border-b border-sheriff-gold/10 last:border-b-0 transition-colors ${
                      op.user_id === operatorId
                        ? "bg-sheriff-gold/15 border-l-2 border-l-sheriff-gold"
                        : "hover:bg-sheriff-gold/5"
                    }`}
                    data-testid={`select-op-${op.user_id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#F5E6B8] truncate">
                        {op.rp_name}
                      </div>
                      <div className="text-[11px] text-[#D4C480]/70 truncate">
                        {op.role}
                        {!op.discord_id && (
                          <span className="ml-2 text-rose-400/80">· No Discord ID</span>
                        )}
                      </div>
                    </div>
                    {op.user_id === operatorId && (
                      <Check className="h-4 w-4 text-sheriff-gold shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          <div>
            <label className="uppercase-label text-sheriff-gold/80 text-[10px] flex items-center justify-between mb-2">
              <span>Nota Interna HR (opzionale)</span>
              <span className="text-[10px] text-[#D4C480]/50 normal-case tracking-normal">
                {note.length}/1800
              </span>
            </label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 1800))}
              placeholder="Nota interna visibile solo alle HR (es. cosa va corretto, riferimenti al rapporto originale, dettagli non condivisibili con l'operatore)…"
              rows={4}
              className="w-full rounded-lg bg-[#0A1F18] border border-sheriff-gold/30 focus:border-sheriff-gold text-[#F5E6B8] px-3 py-2 outline-none transition-colors text-sm resize-none"
              data-testid="hr-note-input"
            />
            <p className="text-[10px] text-[#D4C480]/50 mt-1.5 leading-relaxed">
              Se compilata, verrà pubblicata in un canale Discord riservato alle HR
              (NON visibile all'operatore) insieme al numero del rapporto.
            </p>
          </div>

          <Button
            onClick={submit}
            disabled={submitting || !reportNumber.trim() || !operatorId}
            className="w-full h-11 bg-sheriff-gold text-[#0A1F18] hover:bg-[#C7A957] font-bold"
            data-testid="submit-correction-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio in corso…
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" /> Invia Notifica su Discord
              </>
            )}
          </Button>
        </div>

        {/* Colonna destra: anteprime */}
        <div className="space-y-5">
          {/* Anteprima 1 — EMBED PUBBLICO */}
          <div className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620] p-6">
            <div className="uppercase-label text-sheriff-gold/80 text-[10px] mb-3 flex items-center gap-2">
              <BellRing className="h-3 w-3" /> Anteprima Embed Pubblico
            </div>
            <div className="rounded-lg border-l-4 border-sheriff-gold bg-[#0A1F18] p-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="font-heading font-black text-lg text-[#F5E6B8]">
                  📋 CORREZIONE RAPPORTO
                </div>
                <div className="h-14 w-14 rounded-md bg-sheriff-gold/10 border border-sheriff-gold/30 flex items-center justify-center text-2xl">
                  🛡️
                </div>
              </div>
              <div className="text-sm text-[#D4C480] leading-relaxed space-y-2">
                <div className="text-sheriff-gold">
                  @{selectedOperator?.rp_name || "Operatore"}
                </div>
                <p>
                  Il tuo rapporto ha delle correzioni da fare. Apri un ticket{" "}
                  <span className="text-sheriff-gold font-semibold">
                    Personnel Administration Bureau
                  </span>{" "}
                  dal pannello{" "}
                  <span className="text-sheriff-gold">#servizi-amministrativi</span> per
                  tutti i dettagli.
                </p>
                <p className="text-amber-300/90">
                  ⏳ Hai <span className="font-bold">3 giorni</span> per aprire il ticket,
                  oltre questo termine scatteranno sanzioni disciplinari.
                </p>
                <div className="text-[#F5E6B8]">
                  🆔 <span className="font-mono">Rapporto n.:</span>{" "}
                  <span className="font-mono text-sheriff-gold">
                    {reportNumber.trim() || "—"}
                  </span>
                </div>
              </div>
              <div className="pt-3 border-t border-sheriff-gold/15 text-[11px] text-[#D4C480]/60">
                Personnel Administration Bureau
              </div>
            </div>
            <p className="text-[11px] text-[#D4C480]/50 mt-4 leading-relaxed">
              Postato nel canale pubblico con menzione dell'operatore.
            </p>
          </div>

          {/* Anteprima 2 — EMBED NOTA HR (solo se compilata) */}
          {note.trim() && (
            <div className="rounded-2xl border border-amber-500/30 bg-[#1a1408] p-6">
              <div className="uppercase-label text-amber-300/90 text-[10px] mb-3 flex items-center gap-2">
                <ShieldAlert className="h-3 w-3" /> Anteprima Nota HR (Privata)
              </div>
              <div
                className="rounded-lg border-l-4 border-amber-400 bg-[#0A1F18] p-5 space-y-3"
                data-testid="hr-note-preview"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-heading font-black text-lg text-[#F5E6B8]">
                    📝 NOTA CORREZIONE RAPPORTO
                  </div>
                  <div className="h-14 w-14 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-2xl">
                    🛡️
                  </div>
                </div>
                <div className="text-sm text-[#D4C480] leading-relaxed space-y-1.5">
                  <div>
                    👤 <span className="font-medium">Operatore:</span>{" "}
                    <span className="text-[#F5E6B8]">
                      {selectedOperator?.rp_name || "—"}
                    </span>
                  </div>
                  <div>
                    🆔 <span className="font-medium">Rapporto n.:</span>{" "}
                    <span className="font-mono text-sheriff-gold">
                      {reportNumber.trim() || "—"}
                    </span>
                  </div>
                  <div className="mt-3">
                    <div className="font-medium mb-1">Nota interna:</div>
                    <div className="pl-3 border-l-2 border-amber-400/40 text-[#D4C480]/90 whitespace-pre-wrap">
                      {note.trim() || "—"}
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t border-amber-500/15 text-[11px] text-[#D4C480]/60">
                  Personnel Administration Bureau · Uso interno HR
                </div>
              </div>
              <p className="text-[11px] text-amber-300/60 mt-4 leading-relaxed">
                🔒 Canale riservato alle HR — l'operatore NON vedrà questa nota.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

